import { useEffect, useMemo, useState } from "react";
import { SalonsDrawer } from "./SalonsDrawer";
import type { PlatformApiClient } from "../api";
import { PlatformApiError } from "../api";
import type { OrganizationContext } from "../types";
import { SalonIcon } from "./SalonIcon";
import { ActionButton, ActionToolbar, OverflowMenu } from "../adaptive/actions";
import { OperationalWorkspace, OperationalList, OperationalRow } from "../adaptive/operational";
import type {
  SalonAssistantProductSettings,
  SalonAssistantReadiness,
  SalonAutomationRule,
  SalonAutomationSettings,
  SalonAvailabilityBlock,
  SalonAvailabilityRule,
  SalonBookingRules,
  SalonCalComConnection,
  SalonCalComEventType,
  SalonCalComServiceBinding,
  SalonKnowledgeQueryResult,
  SalonKnowledgeSettings,
  SalonMercadoPagoConnection,
  SalonMetaWhatsAppConnections,
  SalonOverview,
  SalonPublicBookingSettings,
  SalonSchedulingBusiness,
  SalonSchedulingLocation,
  SalonSchedulingResource,
  SalonSchedulingResourceType,
  SalonSchedulingServiceConfig,
  SalonSchedulingSettings,
  SalonServiceCategory,
  SalonServiceResourceRequirement,
} from "./types";

type SettingsModuleState =
  | "complete"
  | "attention"
  | "support"
  | "unavailable"
  | "advanced";

type SettingsModule = Readonly<{
  key: string;
  title: string;
  description: string;
  impact: string;
  state: SettingsModuleState;
  stateLabel: string;
  cta: string;
  icon: string;
  route?: string;
  externalRoute?: string;
  disabled?: boolean;
}>;

const DAYS = [
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
  "Domingo",
] as const;

const RESOURCE_LABELS: Record<SalonSchedulingResourceType, string> = {
  professional: "Profesional",
  room: "Sala / cabina",
  chair: "Silla",
  equipment: "Equipo",
  custom: "Otro recurso",
};

function errorText(error: unknown): string {
  if (error instanceof PlatformApiError) return error.message;
  return "No se pudo completar la operación.";
}

function formatDate(value: string | null | undefined): string {
  if (!value || !Number.isFinite(Date.parse(value))) return "—";
  return new Intl.DateTimeFormat("es-AR", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(value));
}

function inputDateTime(value: string): string {
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return "";
  const date = new Date(parsed);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function dateTimeFromInput(value: string): string {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? new Date(parsed).toISOString() : value;
}

function connectionState(
  value: SalonMetaWhatsAppConnections | null,
  unavailable: boolean,
): Readonly<{
  active: boolean;
  state: SettingsModuleState;
  label: string;
  description: string;
}> {
  if (unavailable) {
    return Object.freeze({
      active: false,
      state: "unavailable",
      label: "No disponible",
      description: "El estado de WhatsApp no está disponible en este entorno.",
    });
  }
  const active =
    value?.connections.filter((item) => item.status === "active") ?? [];
  if (active.length > 0) {
    return Object.freeze({
      active: true,
      state: "complete",
      label: "Conectado",
      description: `${active.length} conexión${active.length === 1 ? "" : "es"} activa${active.length === 1 ? "" : "s"}.`,
    });
  }
  return Object.freeze({
    active: false,
    state: "attention",
    label: "Sin conexión activa",
    description: "No hay una conexión Meta WhatsApp activa para este negocio.",
  });
}

function ModuleCard({
  module,
  navigate,
}: {
  module: SettingsModule;
  navigate: (path: string) => void;
}) {
  const action = module.route ?? module.externalRoute ?? null;
  return (
    <article
      className={`cd-settings2__module-card${module.disabled ? " is-disabled" : ""}`}
    >
      <div className="cd-settings2__module-top">
        <div className="cd-settings2__module-heading">
          <SalonIcon name={module.icon} className="cd-settings2__module-icon" />
          <h3 className="cd-settings2__module-title">{module.title}</h3>
        </div>
        <span
          className={`cd-settings2__state cd-settings2__state--${module.state}`}
        >
          {module.stateLabel}
        </span>
      </div>
      <p className="cd-settings2__module-desc">{module.description}</p>
      <p className="cd-settings2__module-impact">
        <SalonIcon name="arrow_outward" />
        {module.impact}
      </p>
      <button
        type="button"
        className="cd-settings2__module-cta"
        disabled={module.disabled || !action}
        onClick={() => {
          if (action) navigate(action);
        }}
      >
        {module.cta}
        {!module.disabled && action && <SalonIcon name="chevron_right" />}
      </button>
    </article>
  );
}

function ModuleHeader({
  title,
  subtitle,
  state = "Configuración real",
  onBack,
}: {
  title: string;
  subtitle: string;
  state?: string;
  onBack: () => void;
}) {
  return (
    <>
      <button type="button" className="cd-settings2__back" onClick={onBack}>
        <SalonIcon name="arrow_back" />
        Volver
      </button>
      <header className="cd-settings2__hero">
        <div className="cd-settings2__hero-main">
          <div className="cd-settings2__hero-badges">
            <span className="cd-settings2__badge cd-settings2__badge--mvp">
              {state}
            </span>
          </div>
          <h1 className="cd-settings2__hero-title">{title}</h1>
          <p className="cd-settings2__hero-subtitle">{subtitle}</p>
        </div>
      </header>
    </>
  );
}


const AUTOMATION_LABELS: Record<SalonAutomationRule["automationKey"], string> = {
  appointment_reminder: "Recordatorio de turno",
  satisfaction_survey: "Encuesta de satisfacción",
  review_request: "Solicitud de reseña",
  no_show_recovery: "Recuperación de no-show",
};

function AutomationsModule({
  api,
  organizationId,
  settings,
  onBack,
  onExpired,
}: {
  api: PlatformApiClient;
  organizationId: string;
  settings: SalonSchedulingSettings;
  onBack: () => void;
  onExpired: () => void;
}) {
  const [snapshot, setSnapshot] = useState<SalonAutomationSettings | null>(null);
  const [drafts, setDrafts] = useState<readonly SalonAutomationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const next = await api.salonAutomationSettings(organizationId);
      setSnapshot(next);
      setDrafts(next.rules);
    } catch (cause) {
      if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
      else setError(errorText(cause));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); }, [api, organizationId]);

  function update(
    automationKey: SalonAutomationRule["automationKey"],
    mutateRule: (rule: SalonAutomationRule) => SalonAutomationRule,
  ) {
    setDrafts((current) =>
      current.map((rule) =>
        rule.automationKey === automationKey ? mutateRule(rule) : rule,
      ),
    );
  }

  async function save(rule: SalonAutomationRule) {
    setSaving(rule.automationKey);
    setError(null);
    try {
      await api.updateSalonAutomationRule(organizationId, rule);
      await reload();
    } catch (cause) {
      if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
      else setError(errorText(cause));
    } finally {
      setSaving(null);
    }
  }

  return (
    <section className="cd-settings2 cd-settings2--module-state" aria-label="Automatizaciones">
      <ModuleHeader
        title="Automatizaciones"
        subtitle="Recordatorios, encuesta, reseña y recuperación de no-show usando Automation Core."
        state="Automation Core"
        onBack={onBack}
      />
      <BusyError loading={loading} error={error} />
      <section className="cd-settings2__module-preview">
        <header className="cd-settings2__preview-head">
          <div>
            <h2 className="cd-settings2__preview-title">Reglas activas</h2>
            <p className="cd-settings2__preview-sub">
              Canal actual: <strong>WhatsApp</strong>. Cada regla persiste en el runtime canónico.
            </p>
          </div>
          <span className="cd-settings2__state cd-settings2__state--complete">
            {drafts.filter((rule) => rule.enabled).length} activas
          </span>
        </header>

        <div className="cd-settings2__automation-list">
          {drafts.map((rule) => (
            <article key={rule.automationKey} className="cd-settings2__automation-card">
              <div className="cd-settings2__automation-head">
                <div>
                  <strong>{AUTOMATION_LABELS[rule.automationKey]}</strong>
                  <span>{rule.enabled ? "Activa" : "Inactiva"}</span>
                </div>
                <label className="cd-settings2__toggle-row">
                  <input
                    type="checkbox"
                    checked={rule.enabled}
                    onChange={(event) =>
                      update(rule.automationKey, (current) => ({
                        ...current,
                        enabled: event.target.checked,
                      }))
                    }
                  />
                  <span>Habilitada</span>
                </label>
              </div>

              <div className="cd-settings2__preview-form">
                <label className="cd-settings2__field">
                  <span className="cd-settings2__field-label">Timing · minutos</span>
                  <input
                    className="cd-settings2__input"
                    type="number"
                    value={rule.offsetMinutes ?? 0}
                    onChange={(event) =>
                      update(rule.automationKey, (current) => ({
                        ...current,
                        offsetMinutes: Number(event.target.value),
                      }))
                    }
                  />
                </label>
                <label className="cd-settings2__field">
                  <span className="cd-settings2__field-label">Servicios</span>
                  <select
                    className="cd-settings2__input"
                    multiple
                    value={rule.serviceFilter === "all" ? [] : [...rule.serviceFilter]}
                    onChange={(event) => {
                      const selected = [...event.currentTarget.selectedOptions].map((option) => option.value);
                      update(rule.automationKey, (current) => ({
                        ...current,
                        serviceFilter: selected.length === 0 ? "all" : selected,
                      }));
                    }}
                  >
                    {settings.services.map((service) => (
                      <option key={service.id} value={service.id}>{service.name}</option>
                    ))}
                  </select>
                  <small>Sin selección = todos.</small>
                </label>
                <label className="cd-settings2__field">
                  <span className="cd-settings2__field-label">Profesionales</span>
                  <select
                    className="cd-settings2__input"
                    multiple
                    value={rule.resourceFilter === "all" ? [] : [...rule.resourceFilter]}
                    onChange={(event) => {
                      const selected = [...event.currentTarget.selectedOptions].map((option) => option.value);
                      update(rule.automationKey, (current) => ({
                        ...current,
                        resourceFilter: selected.length === 0 ? "all" : selected,
                      }));
                    }}
                  >
                    {settings.resources
                      .filter((resource) => resource.resourceType === "professional")
                      .map((resource) => (
                        <option key={resource.id} value={resource.id}>{resource.displayName ?? resource.name}</option>
                      ))}
                  </select>
                  <small>Sin selección = todos.</small>
                </label>
                <label className="cd-settings2__field">
                  <span className="cd-settings2__field-label">Quiet hours</span>
                  <div className="cd-settings2__inline-fields">
                    <input
                      className="cd-settings2__input"
                      type="time"
                      value={rule.quietHours?.start ?? "22:00"}
                      onChange={(event) =>
                        update(rule.automationKey, (current) => ({
                          ...current,
                          quietHours: {
                            enabled: current.quietHours?.enabled ?? true,
                            start: event.target.value,
                            end: current.quietHours?.end ?? "08:00",
                          },
                        }))
                      }
                    />
                    <input
                      className="cd-settings2__input"
                      type="time"
                      value={rule.quietHours?.end ?? "08:00"}
                      onChange={(event) =>
                        update(rule.automationKey, (current) => ({
                          ...current,
                          quietHours: {
                            enabled: current.quietHours?.enabled ?? true,
                            start: current.quietHours?.start ?? "22:00",
                            end: event.target.value,
                          },
                        }))
                      }
                    />
                  </div>
                  <label className="cd-settings2__toggle-row">
                    <input
                      type="checkbox"
                      checked={rule.quietHours?.enabled ?? false}
                      onChange={(event) =>
                        update(rule.automationKey, (current) => ({
                          ...current,
                          quietHours: {
                            enabled: event.target.checked,
                            start: current.quietHours?.start ?? "22:00",
                            end: current.quietHours?.end ?? "08:00",
                          },
                        }))
                      }
                    />
                    <span>Respetar horario silencioso</span>
                  </label>
                </label>
                <label className="cd-settings2__field cd-settings2__field--wide">
                  <span className="cd-settings2__field-label">Plantilla</span>
                  <textarea
                    className="cd-settings2__input"
                    rows={4}
                    value={rule.templateBody}
                    onChange={(event) =>
                      update(rule.automationKey, (current) => ({
                        ...current,
                        templateBody: event.target.value,
                      }))
                    }
                  />
                </label>
              </div>
              <footer className="cd-settings2__preview-footer">
                <span className="cd-settings2__preview-status">
                  Canal: WhatsApp · filtros de servicio/profesional incluidos.
                </span>
                <button
                  type="button"
                  className="cd-settings2__preview-save"
                  disabled={saving !== null || rule.templateBody.trim().length === 0}
                  onClick={() => void save(rule)}
                >
                  {saving === rule.automationKey ? "Guardando…" : "Guardar regla"}
                </button>
              </footer>
            </article>
          ))}
        </div>
      </section>

      <section className="cd-settings2__module-preview">
        <header className="cd-settings2__preview-head">
          <div>
            <h2 className="cd-settings2__preview-title">Historial reciente</h2>
            <p className="cd-settings2__preview-sub">Últimas ejecuciones persistidas por Automation Core.</p>
          </div>
        </header>
        <div className="cd-settings2__connection-list">
          {(snapshot?.recentRuns ?? []).length === 0 && <p>Sin ejecuciones registradas todavía.</p>}
          {(snapshot?.recentRuns ?? []).map((run) => (
            <article key={run.scheduledActionId + run.attemptedAt} className="cd-settings2__connection-card">
              <div>
                <SalonIcon name={run.status === "sent" ? "check_circle" : "error"} />
                <div>
                  <strong>{run.status}</strong>
                  <span>{formatDate(run.attemptedAt)} · intento {run.attempt}</span>
                </div>
              </div>
              {run.errorCode && <p>{run.errorCode}</p>}
            </article>
          ))}
        </div>
      </section>
    </section>
  );
}

function KnowledgeModule({
  api,
  organizationId,
  onBack,
  onExpired,
}: {
  api: PlatformApiClient;
  organizationId: string;
  onBack: () => void;
  onExpired: () => void;
}) {
  const [snapshot, setSnapshot] = useState<SalonKnowledgeSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [publishNow, setPublishNow] = useState(false);
  const [showTester, setShowTester] = useState(false);
  const [showCreator, setShowCreator] = useState(false);
  const [query, setQuery] = useState("");
  const [queryResult, setQueryResult] = useState<SalonKnowledgeQueryResult | null>(null);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      setSnapshot(await api.salonKnowledgeSettings(organizationId));
    } catch (cause) {
      if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
      else setError(errorText(cause));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); }, [api, organizationId]);

  function closeDocumentCreator() {
    if (saving === "create") return;
    if ((title.trim() || text.trim() || publishNow) && !window.confirm("¿Descartar el documento sin guardar?")) return;
    setShowCreator(false);
    setTitle("");
    setText("");
    setPublishNow(false);
    setError(null);
  }

  async function createDocument() {
    setSaving("create");
    setError(null);
    try {
      await api.createSalonKnowledgeDocument(organizationId, {
        title: title.trim(),
        text: text.trim(),
        status: publishNow ? "approved" : "draft",
      });
      setTitle("");
      setText("");
      setPublishNow(false);
      setShowCreator(false);
      await reload();
    } catch (cause) {
      if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
      else setError(errorText(cause));
    } finally {
      setSaving(null);
    }
  }

  async function changeStatus(
    sourceId: string,
    status: "draft" | "approved" | "archived",
    expectedVersion: number,
  ) {
    setSaving(sourceId);
    setError(null);
    try {
      await api.updateSalonKnowledgeSource(organizationId, sourceId, {
        status,
        expectedVersion,
      });
      await reload();
    } catch (cause) {
      if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
      else setError(errorText(cause));
    } finally {
      setSaving(null);
    }
  }

  async function testKnowledge() {
    setSaving("query");
    setError(null);
    try {
      const result = await api.querySalonKnowledge(organizationId, query.trim());
      setQueryResult(result.result);
    } catch (cause) {
      if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
      else setError(errorText(cause));
    } finally {
      setSaving(null);
    }
  }

  return (
    <section className="cd-settings2 cd-settings2--module-state cd-knowledge-v2" aria-label="Conocimiento de la IA">
      <ModuleHeader
        title="Conocimiento de la IA"
        subtitle="Una biblioteca simple de información aprobada para el asistente."
        state="Conocimiento canónico"
        onBack={onBack}
      />
      <BusyError loading={loading} error={error} />

      <section className="cd-settings2__module-preview">
        <header className="cd-settings2__preview-head">
          <div>
            <h2 className="cd-settings2__preview-title">Fuentes</h2>
            <div className="cd-settings2__preview-sub">
              {snapshot?.readiness.approvedSources ?? 0} documentos disponibles para el asistente.
              <details className="cd-knowledge-v1__technical">
                <summary>Detalle técnico</summary>
                <p>{snapshot?.readiness.approvedChunks ?? 0} fragmentos elegibles para consulta.</p>
              </details>
            </div>
          </div>
        </header>
        <div className="cd-settings2__connection-list">
          {(snapshot?.sources ?? []).length === 0 && <p>Sin fuentes todavía.</p>}
          {(snapshot?.sources ?? []).map((source) => (
            <article key={source.id} className="cd-settings2__connection-card">
              <div>
                <SalonIcon name="description" />
                <div>
                  <strong>{source.title}</strong>
                  <span>{source.status === "approved" ? "Aprobado" : source.status === "draft" ? "Borrador" : "Archivado"} · v{source.version} · {formatDate(source.updatedAt)}</span>
                </div>
              </div>
              <ActionToolbar className="cd-knowledge-v1__actions" label={"Acciones de " + source.title}>
                {source.status === "draft" && <ActionButton
                  variant="tertiary"
                  disabled={saving !== null}
                  onClick={() => void changeStatus(source.id, "approved", source.version)}
                >Publicar</ActionButton>}
                {source.status !== "archived" && <OverflowMenu
                  label={"Más acciones de " + source.title}
                  actions={[
                    ...(source.status === "approved" ? [{
                      key: "draft",
                      label: "Pasar a borrador",
                      disabled: saving !== null,
                      onSelect: () => {
                        if (window.confirm("El documento dejará de estar disponible para el asistente. ¿Continuar?")) {
                          void changeStatus(source.id, "draft", source.version);
                        }
                      },
                    }] : []),
                    {
                      key: "archive",
                      label: "Archivar documento",
                      disabled: saving !== null,
                      destructive: true,
                      onSelect: () => {
                        if (window.confirm("¿Archivar " + source.title + "? Dejará de estar disponible para el asistente.")) {
                          void changeStatus(source.id, "archived", source.version);
                        }
                      },
                    },
                  ]}
                />}
              </ActionToolbar>
            </article>
          ))}
        </div>
      </section>

      <div className="cd-knowledge-v2__actions">
        <button type="button" className={showTester ? "is-active" : ""} aria-expanded={showTester}
          onClick={() => setShowTester((current) => !current)}>
          <SalonIcon name="search" /> {showTester ? "Ocultar prueba" : "Probar conocimiento"}
        </button>
        <button type="button" className="cd-knowledge-v2__primary"
          aria-haspopup="dialog" onClick={() => setShowCreator(true)}>
          <SalonIcon name="add" /> Nuevo documento
        </button>
      </div>
      {showTester && <section className="cd-settings2__module-preview cd-knowledge-v2__workspace">
        <header className="cd-settings2__preview-head">
          <div>
            <h2 className="cd-settings2__preview-title">Probar conocimiento</h2>
            <p className="cd-settings2__preview-sub">Consultá la información aprobada y comprobá qué fuentes respaldan cada respuesta.</p>
          </div>
        </header>
        <div className="cd-settings2__preview-form">
          <label className="cd-settings2__field cd-settings2__field--wide">
            <span className="cd-settings2__field-label">Pregunta</span>
            <input className="cd-settings2__input" value={query} onChange={(event) => setQuery(event.target.value)} />
          </label>
        </div>
        <footer className="cd-settings2__preview-footer">
          <span className="cd-settings2__preview-status">
            {queryResult
              ? queryResult.outcome === "answered"
                ? queryResult.answer
                : "Sin evidencia suficiente · " + queryResult.reason
              : "Todavía no probado."}
          </span>
          <button type="button" className="cd-settings2__preview-save" disabled={saving !== null || !query.trim()} onClick={() => void testKnowledge()}>
            {saving === "query" ? "Probando…" : "Probar conocimiento"}
          </button>
        </footer>
        {queryResult?.outcome === "answered" && (
          <p className="cd-settings2__preview-sub">
            Evidencia: {queryResult.evidence.sourceTitle ?? queryResult.evidence.sourceId} · score {queryResult.score.toFixed(2)}
          </p>
        )}
      </section>}

      {showCreator && <SalonsDrawer
        title="Agregar documento"
        eyebrow="Conocimiento"
        description="El documento se guarda como borrador salvo que elijas publicarlo."
        onClose={closeDocumentCreator}
        busy={saving === "create"}
      >
        <div className="cd-knowledge-v2__create-drawer">
        {error && <div className="inline-alert" role="alert">{error}</div>}
        <div className="cd-settings2__preview-form">
          <label className="cd-settings2__field">
            <span className="cd-settings2__field-label">Título</span>
            <input className="cd-settings2__input" value={title} onChange={(event) => setTitle(event.target.value)} />
          </label>
          <label className="cd-settings2__field cd-settings2__field--wide">
            <span className="cd-settings2__field-label">Contenido</span>
            <textarea className="cd-settings2__input" rows={5} value={text} onChange={(event) => setText(event.target.value)} />
          </label>
          <label className="cd-settings2__toggle-row">
            <input type="checkbox" checked={publishNow} onChange={(event) => setPublishNow(event.target.checked)} />
            <span>Publicar inmediatamente</span>
          </label>
        </div>
        <footer className="cd-settings2__preview-footer">
          <span className="cd-settings2__preview-status">Estado inicial: {publishNow ? "Aprobado" : "Borrador"}</span>
          <button type="button" className="cd-settings2__preview-save" disabled={saving !== null || !title.trim() || !text.trim()} onClick={() => void createDocument()}>
            {saving === "create" ? "Guardando…" : "Guardar documento"}
          </button>
        </footer>
        </div>
      </SalonsDrawer>}

    </section>
  );
}

function PaymentsModule({
  api,
  organizationId,
  settings,
  savingKey,
  mutate,
  onBack,
  onExpired,
}: {
  api: PlatformApiClient;
  organizationId: string;
  settings: SalonSchedulingSettings;
  savingKey: string | null;
  mutate: (key: string, operation: () => Promise<unknown>) => Promise<void>;
  onBack: () => void;
  onExpired: () => void;
}) {
  const rules = settings.bookingRules;
  const [provider, setProvider] = useState<SalonMercadoPagoConnection | null>(null);
  const [providerError, setProviderError] = useState<string | null>(null);
  const [providerBusy, setProviderBusy] = useState(false);
  const [depositMode, setDepositMode] = useState(rules.depositMode);
  const [depositValue, setDepositValue] = useState(rules.depositValue);

  async function reloadProvider() {
    setProviderError(null);
    try {
      setProvider(await api.salonMercadoPagoConnection(organizationId));
    } catch (cause) {
      if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
      else setProviderError(errorText(cause));
    }
  }

  useEffect(() => { void reloadProvider(); }, [api, organizationId]);

  async function connect() {
    setProviderBusy(true);
    setProviderError(null);
    try {
      const result = await api.startSalonMercadoPagoOauth(organizationId);
      window.open(result.authorizationUrl, "_blank", "noopener,noreferrer");
    } catch (cause) {
      if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
      else setProviderError(errorText(cause));
    } finally {
      setProviderBusy(false);
    }
  }

  async function disconnect() {
    setProviderBusy(true);
    setProviderError(null);
    try {
      await api.disconnectSalonMercadoPago(organizationId);
      await reloadProvider();
    } catch (cause) {
      if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
      else setProviderError(errorText(cause));
    } finally {
      setProviderBusy(false);
    }
  }

  return (
    <section className="cd-settings2 cd-settings2--module-state" aria-label="Pagos">
      <ModuleHeader
        title="Pagos"
        subtitle="Métodos manuales, Mercado Pago y política de depósito del negocio."
        state="Payments canónico"
        onBack={onBack}
      />
      <BusyError error={providerError} />

      <section className="cd-settings2__module-preview">
        <header className="cd-settings2__preview-head">
          <div>
            <h2 className="cd-settings2__preview-title">Mercado Pago</h2>
            <p className="cd-settings2__preview-sub">
              OAuth por organización; los secretos nunca llegan al navegador.
            </p>
          </div>
          <span className={"cd-settings2__state " + (
            provider?.connected && provider.runtimeReady && provider.connection?.status === "active"
              ? "cd-settings2__state--complete"
              : "cd-settings2__state--attention"
          )}>
            {!provider
              ? "Leyendo…"
              : !provider.runtimeReady
                ? "Runtime no configurado"
                : provider.connected
                  ? provider.connection?.status ?? "Conectado"
                  : "Sin conectar"}
          </span>
        </header>
        <div className="cd-settings2__connection-list">
          <article className="cd-settings2__connection-card">
            <div>
              <SalonIcon name="payments" />
              <div>
                <strong>Mercado Pago</strong>
                <span>
                  {provider?.connection
                    ? (provider.connection.liveMode ? "Producción" : "Sandbox") +
                      " · actualizado " + formatDate(provider.connection.updatedAt)
                    : "Sin cuenta conectada"}
                </span>
              </div>
            </div>
            <div className="cd-settings2__inline-actions">
              {provider?.connected ? (
                <button
                  type="button"
                  className="cd-settings2__module-cta"
                  disabled={providerBusy}
                  onClick={() => void disconnect()}
                >
                  Desconectar
                </button>
              ) : (
                <button
                  type="button"
                  className="cd-settings2__module-cta"
                  disabled={providerBusy || provider?.runtimeReady === false}
                  onClick={() => void connect()}
                >
                  Conectar Mercado Pago
                </button>
              )}
              <button
                type="button"
                className="cd-settings2__module-cta"
                disabled={providerBusy}
                onClick={() => void reloadProvider()}
              >
                Actualizar estado
              </button>
            </div>
          </article>
        </div>
      </section>

      <section className="cd-settings2__module-preview">
        <header className="cd-settings2__preview-head">
          <div>
            <h2 className="cd-settings2__preview-title">Política de depósito</h2>
            <p className="cd-settings2__preview-sub">
              La misma policy canónica que consume Agenda y checkout de depósitos.
            </p>
          </div>
          <span className="cd-settings2__state cd-settings2__state--complete">
            v{rules.version}
          </span>
        </header>
        <div className="cd-settings2__preview-form">
          <label className="cd-settings2__field">
            <span className="cd-settings2__field-label">Modo</span>
            <select
              className="cd-settings2__input"
              value={depositMode}
              onChange={(event) => {
                const mode = event.target.value as SalonBookingRules["depositMode"];
                setDepositMode(mode);
                if (mode === "none") setDepositValue(0);
              }}
            >
              <option value="none">Sin depósito</option>
              <option value="percentage">Porcentaje</option>
              <option value="fixed">Monto fijo</option>
            </select>
          </label>
          <label className="cd-settings2__field">
            <span className="cd-settings2__field-label">
              {depositMode === "percentage" ? "Porcentaje" : "Valor"}
            </span>
            <input
              className="cd-settings2__input"
              type="number"
              min={0}
              max={depositMode === "percentage" ? 100 : undefined}
              disabled={depositMode === "none"}
              value={depositValue}
              onChange={(event) => setDepositValue(Number(event.target.value))}
            />
          </label>
        </div>
        <footer className="cd-settings2__preview-footer">
          <span className="cd-settings2__preview-status">
            La moneda del monto fijo es la moneda operativa del negocio.
          </span>
          <button
            type="button"
            className="cd-settings2__preview-save"
            disabled={
              savingKey !== null ||
              depositValue < 0 ||
              (depositMode === "percentage" && depositValue > 100)
            }
            onClick={() =>
              void mutate("payments-deposit", () =>
                api.updateSalonBookingRules(organizationId, {
                  depositMode,
                  depositValue,
                  expectedVersion: rules.version,
                }),
              )
            }
          >
            {savingKey === "payments-deposit" ? "Guardando…" : "Guardar depósito"}
          </button>
        </footer>
      </section>

      <section className="cd-settings2__module-preview">
        <header className="cd-settings2__preview-head">
          <div>
            <h2 className="cd-settings2__preview-title">Métodos y permisos</h2>
            <p className="cd-settings2__preview-sub">
              Los cobros manuales no se confunden con pagos provider-backed.
            </p>
          </div>
        </header>
        <ul className="cd-settings2__checklist" aria-label="Métodos de cobro">
          {["Efectivo", "Transferencia bancaria", "Tarjeta externa/manual", "Otro configurado"].map((label) => (
            <li key={label} className="cd-settings2__check is-done">
              <SalonIcon name="check_circle" />
              <span>{label}</span>
            </li>
          ))}
        </ul>
        <div className="cd-settings2__status-card">
          <strong>Reembolsos</strong>
          <p>
            Las mutaciones de refund requieren rol Platform owner/admin en el servidor.
            La UI no puede elevar permisos ni convertir un cobro manual en Mercado Pago.
          </p>
        </div>
      </section>
    </section>
  );
}

function AssistantReadinessModule({
  api,
  organizationId,
  onBack,
  onExpired,
}: {
  api: PlatformApiClient;
  organizationId: string;
  onBack: () => void;
  onExpired: () => void;
}) {
  const [snapshot, setSnapshot] = useState<SalonAssistantReadiness | null>(null);
  const [draft, setDraft] = useState<SalonAssistantProductSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function reload() {
    setError(null);
    try {
      const next = await api.salonAssistantReadiness(organizationId);
      setSnapshot(next);
      setDraft(next.settings);
    } catch (cause) {
      if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
      else setError(errorText(cause));
    }
  }

  useEffect(() => { void reload(); }, [api, organizationId]);

  async function save() {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      await api.updateSalonAssistantSettings(organizationId, {
        enabled: draft.enabled,
        autonomyMode: draft.autonomyMode,
        tone: draft.tone,
        languageMode: draft.languageMode,
        bookingEnabled: draft.bookingEnabled,
        escalationPolicy: draft.escalationPolicy,
        handoffTarget: draft.handoffTarget,
        outOfHoursMode: draft.outOfHoursMode,
        handoffMessage: draft.handoffMessage,
        expectedVersion: draft.version,
      });
      await reload();
    } catch (cause) {
      if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
      else setError(errorText(cause));
    } finally {
      setSaving(false);
    }
  }

  function patch(next: Partial<SalonAssistantProductSettings>) {
    setDraft((current) => current ? { ...current, ...next } : current);
  }

  return (
    <section className="cd-settings2 cd-settings2--module-state" aria-label="Asistente IA">
      <ModuleHeader
        title="Asistente IA"
        subtitle="Configurá la atención automatizada, sus permisos y cuándo interviene una persona."
        state="Configuración del asistente"
        onBack={onBack}
      />
      <BusyError error={error} loading={!snapshot && !error} />

      {snapshot && draft && (
        <>
          <section className="cd-settings2__module-preview">
            <header className="cd-settings2__preview-head">
              <div>
                <h2 className="cd-settings2__preview-title">Estado del asistente</h2>
                <p className="cd-settings2__preview-sub">
                  Activarlo en este negocio no garantiza que el servicio de IA esté disponible.
                </p>
              </div>
              <span className={"cd-settings2__state " + (snapshot.ready && draft.enabled ? "cd-settings2__state--complete" : "cd-settings2__state--attention")}>
                {snapshot.ready && draft.enabled ? "Disponible" : "No disponible"}
              </span>
            </header>
            <div className="cd-settings2__status-card" role="status">
              <p>Configuración del proveedor: <strong>{snapshot.configured ? "Configurada" : "Pendiente"}</strong></p>
              <p>Servicio de IA: <strong>{snapshot.ready ? "Disponible" : "Requiere configuración"}</strong></p>
              <p>Habilitación en el negocio: <strong>{draft.enabled ? "Activada" : "Desactivada"}</strong></p>
              {!snapshot.ready && <>
                <p className="cd-settings2__preview-sub">El asistente no puede ejecutar respuestas automáticas todavía. Pedí al administrador técnico que configure el servicio de IA.</p>
                <p className="cd-settings2__preview-sub">Una conversación puede quedar asignada a “Modo IA” como estado operativo, pero no se automatiza hasta que este servicio esté disponible.</p>
              </>}
              <details>
                <summary>Detalles técnicos</summary>
                <p>Estado: {snapshot.state}</p>
                <p>Perfil: {snapshot.profileVersion ? "v" + snapshot.profileVersion : "Sin versión configurada"}</p>
              </details>
            </div>
          </section>

          <section className="cd-settings2__module-preview">
            <header className="cd-settings2__preview-head">
              <div>
                <h2 className="cd-settings2__preview-title">Comportamiento operativo</h2>
                <p className="cd-settings2__preview-sub">
                  Cambios versionados y auditados por organización.
                </p>
              </div>
              <span className="cd-settings2__state cd-settings2__state--complete">
                v{draft.version}
              </span>
            </header>

            <div className="cd-settings2__preview-form">
              <label className="cd-settings2__toggle-row">
                <input
                  type="checkbox"
                  checked={draft.enabled}
                  onChange={(event) => patch({ enabled: event.target.checked })}
                />
                <span>Habilitar en este negocio</span>
              </label>

              <label className="cd-settings2__field">
                <span className="cd-settings2__field-label">Autonomía</span>
                <select
                  className="cd-settings2__input"
                  value={draft.autonomyMode}
                  onChange={(event) => patch({ autonomyMode: event.target.value as SalonAssistantProductSettings["autonomyMode"] })}
                >
                  <option value="assist">Asistida</option>
                  <option value="autonomous">Autónoma</option>
                </select>
              </label>

              <label className="cd-settings2__field">
                <span className="cd-settings2__field-label">Tono</span>
                <select
                  className="cd-settings2__input"
                  value={draft.tone}
                  onChange={(event) => patch({ tone: event.target.value as SalonAssistantProductSettings["tone"] })}
                >
                  <option value="warm">Cálido</option>
                  <option value="friendly">Amigable</option>
                  <option value="professional">Profesional</option>
                  <option value="concise">Conciso</option>
                </select>
              </label>

              <label className="cd-settings2__field">
                <span className="cd-settings2__field-label">Idioma</span>
                <select
                  className="cd-settings2__input"
                  value={draft.languageMode}
                  onChange={(event) => patch({ languageMode: event.target.value as SalonAssistantProductSettings["languageMode"] })}
                >
                  <option value="customer_detect">Detectar idioma del cliente</option>
                  <option value="business_primary">Idioma principal del negocio</option>
                </select>
              </label>

              <label className="cd-settings2__toggle-row">
                <input
                  type="checkbox"
                  checked={draft.bookingEnabled}
                  onChange={(event) => patch({ bookingEnabled: event.target.checked })}
                />
                <span>Puede gestionar reservas</span>
              </label>

            </div>
            <details className="cd-services-v4__assistant-advanced">
              <summary>Transferencia humana y atención fuera de horario</summary>
              <div className="cd-settings2__preview-form">
              <label className="cd-settings2__field">
                <span className="cd-settings2__field-label">Escalación</span>
                <select
                  className="cd-settings2__input"
                  value={draft.escalationPolicy}
                  onChange={(event) => patch({ escalationPolicy: event.target.value as SalonAssistantProductSettings["escalationPolicy"] })}
                >
                  <option value="human_on_uncertainty">Humano ante incertidumbre</option>
                  <option value="human_on_request">Humano cuando lo pide el cliente</option>
                  <option value="always_human_for_sensitive">Humano para casos sensibles</option>
                </select>
              </label>

              <label className="cd-settings2__field">
                <span className="cd-settings2__field-label">Destino de transferencia</span>
                <input
                  className="cd-settings2__input"
                  value={draft.handoffTarget}
                  onChange={(event) => patch({ handoffTarget: event.target.value })}
                />
              </label>

              <label className="cd-settings2__field">
                <span className="cd-settings2__field-label">Fuera de horario</span>
                <select
                  className="cd-settings2__input"
                  value={draft.outOfHoursMode}
                  onChange={(event) => patch({ outOfHoursMode: event.target.value as SalonAssistantProductSettings["outOfHoursMode"] })}
                >
                  <option value="queue">Encolar para el equipo</option>
                  <option value="message_only">Sólo informar</option>
                </select>
              </label>

              <label className="cd-settings2__field cd-settings2__field--wide">
                <span className="cd-settings2__field-label">Mensaje de transferencia</span>
                <textarea
                  className="cd-settings2__input"
                  rows={3}
                  value={draft.handoffMessage}
                  onChange={(event) => patch({ handoffMessage: event.target.value })}
                />
              </label>
              </div>
            </details>

            <footer className="cd-settings2__preview-footer cd-assistant-v4__footer">
              <span className="cd-settings2__preview-status">
                {draft.persisted
                  ? "Última actualización " + formatDate(draft.updatedAt)
                  : "Todavía usa defaults; guardar crea la configuración v1."}
              </span>
              <ActionButton
                variant="primary"
                loading={saving}
                disabled={
                  saving ||
                  !draft.handoffTarget.trim() ||
                  !draft.handoffMessage.trim()
                }
                disabledReason={!draft.handoffTarget.trim() || !draft.handoffMessage.trim() ? "Completá el destino y mensaje de transferencia." : undefined}
                onClick={() => void save()}
              >
                Guardar configuración
              </ActionButton>
            </footer>
          </section>
        </>
      )}
    </section>
  );
}

function BusyError({
  error,
  loading,
}: {
  error: string | null;
  loading?: boolean;
}) {
  return (
    <>
      {loading && <div className="inline-alert">Leyendo configuración…</div>}
      {error && (
        <div className="inline-alert" role="alert">
          {error}
        </div>
      )}
    </>
  );
}

function BusinessModule({
  settings,
  savingKey,
  mutate,
  api,
  organizationId,
  onBack,
}: {
  settings: SalonSchedulingSettings;
  savingKey: string | null;
  mutate: (key: string, operation: () => Promise<unknown>) => Promise<void>;
  api: PlatformApiClient;
  organizationId: string;
  onBack: () => void;
}) {
  const business = settings.business;
  const [publicName, setPublicName] = useState(business.publicName);
  const [phone, setPhone] = useState(business.phone ?? "");
  const [email, setEmail] = useState(business.email ?? "");
  const [timezone, setTimezone] = useState(business.timezone);
  const [currency, setCurrency] = useState(business.defaultCurrency);
  const [language, setLanguage] = useState(business.primaryLanguage);
  const [description, setDescription] = useState(
    business.publicDescription ?? "",
  );
  const [newLocationName, setNewLocationName] = useState("");
  const [newLocationOpen, setNewLocationOpen] = useState(false);

  return (
    <section className="cd-settings2 cd-settings2--module-state">
      <ModuleHeader
        title="Negocio y locales"
        subtitle="Identidad operativa, sucursales y datos que usan Scheduling, reserva online y la IA."
        onBack={onBack}
      />
      <section className="cd-settings2__module-preview">
        <header className="cd-settings2__preview-head">
          <div>
            <h2 className="cd-settings2__preview-title">Perfil del negocio</h2>
            <p className="cd-settings2__preview-sub">
              Esta información pertenece a Salons; usuarios, membresías y
              seguridad siguen perteneciendo a Platform.
            </p>
          </div>
          <span className="cd-settings2__state cd-settings2__state--complete">
            v{business.version}
          </span>
        </header>
        <div className="cd-settings2__preview-form">
          <label className="cd-settings2__field">
            <span className="cd-settings2__field-label">Nombre público</span>
            <input
              className="cd-settings2__input"
              value={publicName}
              onChange={(event) => setPublicName(event.target.value)}
            />
          </label>
          <label className="cd-settings2__field">
            <span className="cd-settings2__field-label">Teléfono</span>
            <input
              className="cd-settings2__input"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
            />
          </label>
          <label className="cd-settings2__field">
            <span className="cd-settings2__field-label">Email</span>
            <input
              className="cd-settings2__input"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          <label className="cd-settings2__field">
            <span className="cd-settings2__field-label">Zona horaria</span>
            <input
              className="cd-settings2__input"
              value={timezone}
              onChange={(event) => setTimezone(event.target.value)}
            />
          </label>
          <label className="cd-settings2__field">
            <span className="cd-settings2__field-label">Moneda</span>
            <input
              className="cd-settings2__input"
              value={currency}
              maxLength={3}
              onChange={(event) =>
                setCurrency(event.target.value.toUpperCase())
              }
            />
          </label>
          <label className="cd-settings2__field">
            <span className="cd-settings2__field-label">Idioma</span>
            <input
              className="cd-settings2__input"
              value={language}
              onChange={(event) => setLanguage(event.target.value)}
            />
          </label>
          <label className="cd-settings2__field cd-settings2__field--wide">
            <span className="cd-settings2__field-label">
              Descripción pública
            </span>
            <textarea
              className="cd-settings2__input"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </label>
        </div>
        <footer className="cd-settings2__preview-footer">
          <span className="cd-settings2__preview-status">
            Configuración canónica del producto.
          </span>
          <button
            type="button"
            className="cd-settings2__preview-save"
            disabled={savingKey !== null || !publicName.trim()}
            onClick={() =>
              void mutate("business", () =>
                api.updateSalonBusiness(organizationId, {
                  publicName: publicName.trim(),
                  phone: phone.trim() || null,
                  email: email.trim() || null,
                  timezone: timezone.trim(),
                  defaultCurrency: currency.trim(),
                  primaryLanguage: language.trim(),
                  publicDescription: description.trim() || null,
                  expectedVersion: business.version,
                }),
              )
            }
          >
            {savingKey === "business" ? "Guardando…" : "Guardar negocio"}
          </button>
        </footer>
      </section>

      <div className="cd-settings2__group">
        <div className="cd-settings2__group-head">
          <h2 className="cd-settings2__group-title">Locales</h2>
          <p className="cd-settings2__group-hint">
            Cada turno, recurso y venta conserva su local.
          </p>
        </div>
        <div className="cd-settings2__services-grid">
          {settings.locations.map((location) => (
            <LocationEditor
              key={`${location.id}:${location.version}`}
              location={location}
              saving={savingKey === `location:${location.id}`}
              onSave={(input) =>
                mutate(`location:${location.id}`, () =>
                  api.updateSalonLocation(
                    organizationId,
                    location.id,
                    input,
                  ),
                )
              }
            />
          ))}
        </div>
        <button type="button" className="cd-btn-primary cd-settings2__new-location-trigger"
          onClick={() => setNewLocationOpen(true)}>+ Nuevo local</button>
        {newLocationOpen && <SalonsDrawer
          title="Nuevo local"
          eyebrow="Negocio y locales"
          description="Agregá una sucursal con la zona horaria del negocio."
          busy={savingKey === "location:new"}
          onClose={() => {
            if (savingKey === "location:new") return;
            if (newLocationName.trim() && !window.confirm("¿Descartar el nuevo local sin guardar?")) return;
            setNewLocationOpen(false);
            setNewLocationName("");
          }}
        >
          <div className="cd-settings2__new-location-drawer">
          <div className="cd-settings2__preview-form">
            <label className="cd-settings2__field">
              <span className="cd-settings2__field-label">Nombre del local</span>
              <input
                className="cd-settings2__input"
                placeholder="Centro"
                value={newLocationName}
                onChange={(event) => setNewLocationName(event.target.value)}
              />
            </label>
          </div>
          <footer className="cd-settings2__preview-footer">
            <span className="cd-settings2__preview-status">
              Se crea con la zona horaria del negocio.
            </span>
            <button
              type="button"
              className="cd-settings2__preview-save"
              disabled={savingKey !== null || !newLocationName.trim()}
              onClick={() =>
                void mutate("location:new", async () => {
                  await api.createSalonLocation(organizationId, {
                    name: newLocationName.trim(),
                    timezone: business.timezone,
                  });
                  setNewLocationName("");
                  setNewLocationOpen(false);
                })
              }
            >
              {savingKey === "location:new" ? "Creando…" : "Crear local"}
            </button>
          </footer>
          </div>
        </SalonsDrawer>}
      </div>
    </section>
  );
}

function LocationEditor({
  location,
  saving,
  onSave,
}: {
  location: SalonSchedulingLocation;
  saving: boolean;
  onSave: (
    input: Partial<Omit<SalonSchedulingLocation, "id" | "version">> & {
      expectedVersion: number;
    },
  ) => Promise<void>;
}) {
  const [name, setName] = useState(location.name);
  const [address, setAddress] = useState(location.address ?? "");
  const [timezone, setTimezone] = useState(location.timezone);
  const [active, setActive] = useState(location.active);
  const [online, setOnline] = useState(location.onlineBookingEnabled);

  return (
    <article className="cd-settings2__module-preview">
      <header className="cd-settings2__preview-head">
        <div>
          <h3 className="cd-settings2__preview-title">{location.name}</h3>
          <p className="cd-settings2__preview-sub">
            {location.address || "Sin dirección cargada"}
          </p>
        </div>
        <span
          className={`cd-settings2__state cd-settings2__state--${active ? "complete" : "attention"}`}
        >
          {active ? "Activo" : "Inactivo"}
        </span>
      </header>
      <div className="cd-settings2__preview-form">
        <label className="cd-settings2__field">
          <span className="cd-settings2__field-label">Nombre</span>
          <input
            className="cd-settings2__input"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <label className="cd-settings2__field">
          <span className="cd-settings2__field-label">Dirección</span>
          <input
            className="cd-settings2__input"
            value={address}
            onChange={(event) => setAddress(event.target.value)}
          />
        </label>
        <label className="cd-settings2__field">
          <span className="cd-settings2__field-label">Zona horaria</span>
          <input
            className="cd-settings2__input"
            value={timezone}
            onChange={(event) => setTimezone(event.target.value)}
          />
        </label>
        <label className="cd-settings2__toggle-row">
          <span>Activo</span>
          <input
            type="checkbox"
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
            className="cd-settings2__checkbox"
          />
        </label>
        <label className="cd-settings2__toggle-row">
          <span>Reserva online</span>
          <input
            type="checkbox"
            checked={online}
            onChange={(event) => setOnline(event.target.checked)}
            className="cd-settings2__checkbox"
          />
        </label>
      </div>
      <footer className="cd-settings2__preview-footer">
        <span className="cd-settings2__preview-status">
          Local · versión {location.version}
        </span>
        <button
          type="button"
          className="cd-settings2__preview-save"
          disabled={saving || !name.trim()}
          onClick={() =>
            void onSave({
              name: name.trim(),
              address: address.trim() || null,
              timezone: timezone.trim(),
              active,
              onlineBookingEnabled: online,
              expectedVersion: location.version,
            })
          }
        >
          {saving ? "Guardando…" : "Guardar local"}
        </button>
      </footer>
    </article>
  );
}

function ServicesModule({
  settings,
  savingKey,
  mutate,
  api,
  organizationId,
  onBack,
}: {
  settings: SalonSchedulingSettings;
  savingKey: string | null;
  mutate: (key: string, operation: () => Promise<unknown>) => Promise<void>;
  api: PlatformApiClient;
  organizationId: string;
  onBack: () => void;
}) {
  const [newCategory, setNewCategory] = useState("");
  const [newService, setNewService] = useState("");
  const [newServiceCategory, setNewServiceCategory] = useState("");
  const [newServiceDuration, setNewServiceDuration] = useState("45");
  const [newServicePrice, setNewServicePrice] = useState("");
  const [newServiceOnline, setNewServiceOnline] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editorDirty, setEditorDirty] = useState(false);
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [newCategoryOpen, setNewCategoryOpen] = useState(false);

  function resetNewService() {
    setNewService("");
    setNewServiceCategory("");
    setNewServiceDuration("45");
    setNewServicePrice("");
    setNewServiceOnline(true);
  }

  function closeNewService() {
    if (savingKey !== null) return;
    const dirty = Boolean(newService.trim() || newServiceCategory || newServicePrice.trim() ||
      newServiceDuration !== "45" || !newServiceOnline);
    if (dirty && !window.confirm("¿Descartar el servicio sin guardar?")) return;
    resetNewService();
    setCreateOpen(false);
  }

  function closeNewCategory() {
    if (savingKey !== null) return;
    if (newCategory.trim() && !window.confirm("¿Descartar la categoría sin guardar?")) return;
    setNewCategory("");
    setNewCategoryOpen(false);
  }

  const selectedService = settings.services.find((service) => service.id === selectedServiceId);
  const visibleServices = settings.services.filter((service) => {
    const matchesName = service.name.toLocaleLowerCase("es").includes(search.trim().toLocaleLowerCase("es"));
    const matchesStatus = statusFilter === "all" || (statusFilter === "active" ? service.active : !service.active);
    const matchesCategory = categoryFilter === "all" || (service.categoryId ?? "uncategorized") === categoryFilter;
    return matchesName && matchesStatus && matchesCategory;
  });

  const serviceGroups = [
    ...settings.categories.slice().sort((left, right) => left.sortOrder - right.sortOrder)
      .map((category) => ({ id: category.id, name: category.name,
        items: visibleServices.filter((service) => service.categoryId === category.id) })),
    { id: "uncategorized", name: "Sin categoría",
      items: visibleServices.filter((service) =>
        !service.categoryId || !settings.categories.some((category) => category.id === service.categoryId)) },
  ].filter((group) => group.items.length > 0);

  useEffect(() => { setEditorDirty(false); }, [selectedService?.id, selectedService?.version]);


  function closeEditor() {
    if (savingKey) return;
    if (editorDirty && !window.confirm("Tenés cambios sin guardar. ¿Descartarlos?")) return;
    setSelectedServiceId(null);
    setEditorDirty(false);
  }

  function selectService(serviceId: string) {
    if (selectedServiceId === serviceId) return;
    if (editorDirty && !window.confirm("Tenés cambios sin guardar. ¿Descartarlos y abrir otro servicio?")) return;
    setSelectedServiceId(serviceId);
    setCreateOpen(false);
    setEditorDirty(false);
  }

  return (
    <section className="cd-settings2 cd-settings2--module-state cd-services-v1 cd-services-v3">
      <header className="cd-services-v3__page-header">
        <div className="cd-services-v3__heading">
          <button type="button" className="cd-settings2__back" onClick={onBack}>
            <SalonIcon name="arrow_back" /> Volver
          </button>
          <h1>Servicios</h1>
          <p>Gestioná el catálogo, los precios y la disponibilidad de tu negocio.</p>
        </div>
        <ActionToolbar label="Acciones de servicios">
          <ActionButton
            variant="primary"
            disabled={savingKey !== null}
            onClick={() => {
              if (editorDirty && !window.confirm("Tenés cambios sin guardar. ¿Descartarlos y crear un servicio?")) return;
              setSelectedServiceId(null);
              setCreateOpen(true);
              setEditorDirty(false);
            }}
          >+ Nuevo servicio</ActionButton>
        </ActionToolbar>
      </header>


      {createOpen && <SalonsDrawer
        title="Nuevo servicio"
        eyebrow="Catálogo / Crear"
        description="Definí los datos principales. Podrás ajustar recursos y tiempos avanzados después."
        busy={savingKey !== null}
        onClose={closeNewService}
      >
        <div className="cd-services-v4__create-form">
          <p className="cd-services-v4__form-caption">Información esencial</p>
          <label htmlFor="salons-create-service">Nombre del servicio</label>
          <input id="salons-create-service" className="cd-settings2__input"
            value={newService} placeholder="Ej.: Tratamiento capilar"
            onChange={(event) => setNewService(event.target.value)} />
          <div className="cd-services-v4__create-grid">
            <label> Categoría
              <select className="cd-settings2__input" value={newServiceCategory}
                onChange={(event) => setNewServiceCategory(event.target.value)}>
                <option value="">Sin categoría</option>
                {settings.categories.filter((category) => category.active).map((category) =>
                  <option key={category.id} value={category.id}>{category.name}</option>)}
              </select>
            </label>
            <label> Duración (min)
              <input type="number" className="cd-settings2__input" min={5} max={720} step={5}
                value={newServiceDuration} onChange={(event) => setNewServiceDuration(event.target.value)} />
            </label>
            <label> Precio (ARS)
              <input type="number" className="cd-settings2__input" min={0} step="0.01"
                value={newServicePrice} placeholder="Ej.: 18000"
                onChange={(event) => setNewServicePrice(event.target.value)} />
            </label>
          </div>
          <div className="cd-services-v4__create-options">
            <label className="cd-settings2__toggle-row">
              <input type="checkbox" checked={newServiceOnline}
                onChange={(event) => setNewServiceOnline(event.target.checked)} />
              <span>Permitir reserva online</span>
            </label>
            <p>Los profesionales y requisitos técnicos se pueden asignar desde el editor.</p>
          </div>
        </div>
        <footer className="cd-services-v4__drawer-footer">
          <ActionButton variant="ghost" onClick={closeNewService} disabled={savingKey !== null}>Cancelar</ActionButton>
          <ActionButton variant="primary" loading={savingKey === "service:new"}
            disabled={savingKey !== null || !newService.trim() ||
              !Number.isSafeInteger(Number(newServiceDuration)) || Number(newServiceDuration) < 5 ||
              Number(newServiceDuration) > 720 ||
              (newServicePrice.trim() !== "" && (!Number.isFinite(Number(newServicePrice)) || Number(newServicePrice) < 0))}
            disabledReason={!newService.trim() ? "Ingresá un nombre para crear el servicio." : undefined}
            onClick={() => void mutate("service:new", async () => {
              await api.createSalonServiceConfig(organizationId, {
                name: newService.trim(),
                durationMinutes: Number(newServiceDuration),
                priceMinor: newServicePrice.trim() === "" ? null : Math.round(Number(newServicePrice) * 100),
                categoryId: newServiceCategory || null,
                publicBookingEnabled: newServiceOnline,
                currency: settings.business.defaultCurrency,
                locationIds: settings.locations.filter((location) => location.active).map((location) => location.id),
              });
              setNewService("");
              setNewServiceCategory("");
              setNewServiceDuration("45");
              setNewServicePrice("");
              setNewServiceOnline(true);
              setCreateOpen(false);
            })}
          >Crear servicio</ActionButton>
        </footer>
      </SalonsDrawer>}


      <section className="cd-services-v3__catalog" aria-label="Catálogo de servicios">
        <div className="cd-services-v3__catalog-header">
          <div>
            <h2>Catálogo de servicios</h2>
            <p>{settings.services.length} servicios · {settings.categories.length} categorías</p>
          </div>
          <button type="button" className="cd-services-v3__manage-categories"
            aria-expanded={categoriesOpen} onClick={() => setCategoriesOpen((current) => !current)}>
            <SalonIcon name="category" />
            <span className="cd-services-v3__manage-desktop">{categoriesOpen ? "Cerrar categorías" : "Administrar categorías"}</span>
            <span className="cd-services-v3__manage-mobile">{categoriesOpen ? "Cerrar" : "Categorías"}</span>
          </button>
        </div>
      <div className="cd-services-v1__filters cd-services-v3__filters">
        <label className="cd-services-v1__search">
          <span className="cd-services-v3__control-label">Buscar servicio</span>
          <input
            type="search"
            value={search}
            placeholder="Nombre del servicio…"
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <label>
          <span className="cd-services-v3__control-label">Estado</span>
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as "all" | "active" | "inactive")}>
            <option value="all">Todos</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </select>
        </label>
      </div>

        {categoriesOpen && <section className="cd-services-v1__categories" aria-label="Administrar categorías">
        <div className="cd-services-v3__category-manager-heading"><h3>Administrar categorías</h3><p>Editá el nombre, estado y orden de cada categoría.</p></div>
        <div className="cd-settings2__connection-list">
          {settings.categories.map((category) => (
            <CategoryEditor
              key={category.id + ":" + category.version}
              category={category}
              saving={savingKey === "category:" + category.id}
              onSave={(input) =>
                mutate("category:" + category.id, () =>
                  api.updateSalonServiceCategory(organizationId, category.id, input),
                )
              }
            />
          ))}
        </div>
        <div className="cd-services-v1__create-category cd-services-v4__create-category">
          <button type="button" className="cd-services-v4__category-add"
            aria-haspopup="dialog" onClick={() => setNewCategoryOpen(true)}>
            <SalonIcon name="add" /> Agregar categoría
          </button>
          {newCategoryOpen && <SalonsDrawer
            title="Nueva categoría"
            eyebrow="Servicios"
            description="Creá una categoría para organizar el catálogo."
            onClose={closeNewCategory}
            busy={savingKey === "category:new"}
          ><div className="cd-services-v4__new-category-fields">
          <label htmlFor="salons-new-category">Nombre de la categoría</label>
          <input
            id="salons-new-category"
            className="cd-settings2__input"
            value={newCategory}
            placeholder="Cabello"
            onChange={(event) => setNewCategory(event.target.value)}
          />
          <footer className="cd-services-v4__drawer-footer cd-services-v4__new-category-footer">
            <ActionButton variant="ghost" disabled={savingKey !== null} onClick={closeNewCategory}>Cancelar</ActionButton>
            <ActionButton
              variant="primary"
              loading={savingKey === "category:new"}
              disabled={savingKey !== null || !newCategory.trim()}
              onClick={() => void mutate("category:new", async () => {
                await api.createSalonServiceCategory(organizationId, { name: newCategory.trim() });
                setNewCategory("");
                setNewCategoryOpen(false);
              })}
            >Crear categoría</ActionButton>
          </footer>
          </div></SalonsDrawer>}
        </div>
      </section>}


      <OperationalWorkspace
        className={"cd-services-v1__workspace" + (selectedService ? " cd-services-v1__workspace--selected" : "")}
        list={<section className="cd-services-v1__list" aria-label="Catálogo por categorías">
          <label className="cd-services-v4__mobile-category">
            <span>Categoría</span>
            <select aria-label="Filtrar servicios por categoría" value={categoryFilter}
              onChange={(event) => setCategoryFilter(event.target.value)}>
              <option value="all">Todas las categorías ({settings.services.length})</option>
              {settings.categories.slice().sort((a,b)=>a.sortOrder-b.sortOrder).map((category)=>(
                <option key={category.id} value={category.id}>{category.name} ({settings.services.filter((service)=>service.categoryId===category.id).length})</option>
              ))}
              {settings.services.some((service)=>!service.categoryId) && <option value="uncategorized">Sin categoría</option>}
            </select>
          </label>
          <nav className="cd-services-v2__category-tabs cd-services-v3__tabs" aria-label="Filtrar servicios por categoría">
            <button type="button" className={categoryFilter === "all" ? "is-active" : ""}
              aria-pressed={categoryFilter === "all"} onClick={() => setCategoryFilter("all")}>
              Todos <span>{settings.services.length}</span>
            </button>
            {settings.categories.slice().sort((left, right) => left.sortOrder - right.sortOrder).map((category) => (
              <button key={category.id} type="button"
                className={categoryFilter === category.id ? "is-active" : ""}
                aria-pressed={categoryFilter === category.id}
                onClick={() => setCategoryFilter(category.id)}>
                {category.name} <span>{settings.services.filter((service) => service.categoryId === category.id).length}</span>
              </button>
            ))}
            {settings.services.some((service) => !service.categoryId) && <button
              type="button" className={categoryFilter === "uncategorized" ? "is-active" : ""}
              aria-pressed={categoryFilter === "uncategorized"}
              onClick={() => setCategoryFilter("uncategorized")}>Sin categoría</button>}
          </nav>
          {visibleServices.length === 0
            ? <div className="cd-services-v1__empty">
                <p>{settings.services.length === 0 ? "Todavía no hay servicios." : "No encontramos servicios con estos filtros."}</p>
                {settings.services.length > 0 && <ActionButton variant="ghost" onClick={() => { setSearch(""); setStatusFilter("all"); setCategoryFilter("all"); }}>Limpiar filtros</ActionButton>}
              </div>
            : serviceGroups.map((group) => <section className="cd-services-v2__group" key={group.id} aria-label={group.name}>
                <header className="cd-services-v2__group-head">
                  <h3>{group.name}</h3><span>{group.items.length} servicio{group.items.length === 1 ? "" : "s"}</span>
                </header>
                <div className="cd-services-v1__column-labels cd-services-v3__columns" aria-hidden="true">
                  <span>Servicio</span><span>Duración</span><span>Precio</span>
                </div>
                <OperationalList ariaLabel={"Servicios · " + group.name}>
                  {group.items.map((service) => <OperationalRow key={service.id}
                    selected={selectedService?.id === service.id}
                    ariaLabel={"Editar " + service.name}
                    onSelect={() => selectService(service.id)}>
                    <span className="cd-services-v1__row cd-services-v3__row">
                      <span className="cd-services-v1__row-name">
                        <strong>{service.name}</strong>
                        <small>{service.active ? "Activo" : "Inactivo"}</small>
                      </span>
                      <span>{service.durationMinutes} min</span>
                      <span>{service.priceMinor == null ? "Sin precio" : new Intl.NumberFormat("es-AR", { style: "currency", currency: service.currency, maximumFractionDigits: 0 }).format(service.priceMinor / 100)}</span>
                    </span>
                  </OperationalRow>)}
                </OperationalList>
              </section>)}
        </section>}
      />
      </section>
      {selectedService && <SalonsDrawer
        title={selectedService.name}
        eyebrow="Catálogo / Editar servicio"
        description="Modificá la información, el precio y la disponibilidad."
        onClose={closeEditor}
        busy={savingKey !== null}
        width="wide"
      >
        <div onChangeCapture={() => setEditorDirty(true)}>
          <ServiceConfigEditor
            key={selectedService.id + ":" + selectedService.version}
            service={selectedService}
            settings={settings}
            saving={savingKey === "service:" + selectedService.id}
            onSave={(input) => mutate("service:" + selectedService.id,
              () => api.updateSalonServiceConfig(organizationId, selectedService.id, input))}
          />
        </div>
      </SalonsDrawer>}



    </section>
  );
}

function CategoryEditor({
  category,
  saving,
  onSave,
}: {
  category: SalonServiceCategory;
  saving: boolean;
  onSave: (
    input: Partial<Omit<SalonServiceCategory, "id" | "version">> & {
      expectedVersion: number;
    },
  ) => Promise<void>;
}) {
  const [name, setName] = useState(category.name);
  const [active, setActive] = useState(category.active);
  const [sortOrder, setSortOrder] = useState(String(category.sortOrder));
  const [editing, setEditing] = useState(false);
  const dirty = name.trim() !== category.name || active !== category.active || Number(sortOrder) !== category.sortOrder;
  return (
    <article className="cd-services-v4__category">
      <div className="cd-services-v4__category-summary">
        <span className="cd-services-v4__category-icon"><SalonIcon name="category" /></span>
        <div>
          <strong>{category.name}</strong>
          <small>{category.active ? "Visible" : "Oculta"} · Orden {category.sortOrder}</small>
        </div>
        <button type="button" aria-expanded={editing} onClick={() => setEditing((current) => !current)}
          className="cd-services-v4__category-edit">{editing ? "Cerrar" : "Editar"}</button>
      </div>
      {editing && <div className="cd-services-v4__category-form">
        <label>Nombre
          <input className="cd-settings2__input" value={name} onChange={(event) => setName(event.target.value)} />
        </label>
        <label>Orden
          <input className="cd-settings2__input" type="number" min={0} step={1}
            value={sortOrder} onChange={(event) => setSortOrder(event.target.value)} />
        </label>
        <label className="cd-settings2__toggle-row">
          <span>Visible en el catálogo</span>
          <input type="checkbox" checked={active} onChange={(event) => setActive(event.target.checked)} />
        </label>
        <div className="cd-services-v4__category-actions">
          <button type="button" onClick={() => {
            setName(category.name);
            setActive(category.active);
            setSortOrder(String(category.sortOrder));
            setEditing(false);
          }}>Cancelar</button>
          <button type="button" className="cd-services-v4__category-save"
            disabled={saving || !name.trim() || !Number.isSafeInteger(Number(sortOrder)) || Number(sortOrder) < 0 || !dirty}
            onClick={() => void onSave({name:name.trim(),active,sortOrder:Number(sortOrder),expectedVersion:category.version})}>
            {saving ? "Guardando…" : "Guardar categoría"}
          </button>
        </div>
      </div>}
    </article>
  );
}

function toggleId(
  values: readonly string[],
  value: string,
  enabled: boolean,
): readonly string[] {
  if (enabled) {
    return values.includes(value) ? values : [...values, value];
  }
  return values.filter((item) => item !== value);
}

function ServiceConfigEditor({
  service,
  settings,
  saving,
  onSave,
}: {
  service: SalonSchedulingServiceConfig;
  settings: SalonSchedulingSettings;
  saving: boolean;
  onSave: (
    input: Partial<Omit<SalonSchedulingServiceConfig, "id" | "version">> & {
      expectedVersion: number;
    },
  ) => Promise<void>;
}) {
  const [name, setName] = useState(service.name);
  const [description, setDescription] = useState(service.description ?? "");
  const [duration, setDuration] = useState(String(service.durationMinutes));
  const [bufferBefore, setBufferBefore] = useState(
    String(service.bufferBeforeMinutes),
  );
  const [bufferAfter, setBufferAfter] = useState(
    String(service.bufferAfterMinutes),
  );
  const [price, setPrice] = useState(
    service.priceMinor == null ? "" : String(service.priceMinor / 100),
  );
  const [currency, setCurrency] = useState(service.currency);
  const [active, setActive] = useState(service.active);
  const [publicBooking, setPublicBooking] = useState(
    service.publicBookingEnabled,
  );
  const [categoryId, setCategoryId] = useState(service.categoryId ?? "");
  const [locationIds, setLocationIds] = useState<readonly string[]>(
    service.locationIds,
  );
  const [eligibleResourceIds, setEligibleResourceIds] = useState<
    readonly string[]
  >(service.eligibleResourceIds);
  const [requirements, setRequirements] = useState<
    readonly SalonServiceResourceRequirement[]
  >(service.resourceRequirements);

  const addRequirement = () =>
    setRequirements([
      ...requirements,
      {
        requirementKey: `resource-${requirements.length + 1}`,
        resourceType: "professional",
        capability: null,
        quantity: 1,
        required: true,
        selectionMode: "automatic",
      },
    ]);

  const updateRequirement = (
    index: number,
    patch: Partial<SalonServiceResourceRequirement>,
  ) =>
    setRequirements(
      requirements.map((requirement, current) =>
        current === index ? { ...requirement, ...patch } : requirement,
      ),
    );

  return (
    <article className="cd-settings2__module-preview">
      <header className="cd-settings2__preview-head">
        <div>
          <h3 className="cd-settings2__preview-title">{service.name}</h3>
          <p className="cd-settings2__preview-sub">
            {service.priceMinor == null
              ? "Sin precio canónico"
              : `${service.currency} ${(service.priceMinor / 100).toLocaleString("es-AR")}`}
          </p>
        </div>
        <span
          className={`cd-settings2__state cd-settings2__state--${active ? "complete" : "attention"}`}
        >
          {active ? "Activo" : "Pausado"}
        </span>
      </header>

      <section className="cd-services-v4__essential">
        <div className="cd-services-v4__section-heading"><h3>Información esencial</h3><p>Lo que ve el cliente al reservar.</p></div>
      <div className="cd-settings2__preview-form cd-settings2__service-form">
        <label className="cd-settings2__field">
          <span className="cd-settings2__field-label">Nombre</span>
          <input
            className="cd-settings2__input"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <label className="cd-settings2__field">
          <span className="cd-settings2__field-label">Categoría</span>
          <select
            className="cd-settings2__input"
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value)}
          >
            <option value="">Sin categoría</option>
            {settings.categories
              .filter((category) => category.active)
              .map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
          </select>
        </label>
        <label className="cd-settings2__field">
          <span className="cd-settings2__field-label">Duración</span>
          <input
            className="cd-settings2__input"
            type="number"
            min={5}
            max={720}
            step={5}
            value={duration}
            onChange={(event) => setDuration(event.target.value)}
          />
        </label>
        <label className="cd-settings2__field">
          <span className="cd-settings2__field-label">Precio</span>
          <input
            className="cd-settings2__input"
            type="number"
            min={0}
            step="0.01"
            value={price}
            onChange={(event) => setPrice(event.target.value)}
          />
        </label>
        <label className="cd-settings2__field">
          <span className="cd-settings2__field-label">Moneda</span>
          <input
            className="cd-settings2__input"
            maxLength={3}
            value={currency}
            onChange={(event) =>
              setCurrency(event.target.value.toUpperCase())
            }
          />
        </label>
        <label className="cd-settings2__field cd-settings2__field--wide">
          <span className="cd-settings2__field-label">Descripción</span>
          <textarea
            className="cd-settings2__input"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>
        <label className="cd-settings2__toggle-row">
          <span>Activo</span>
          <input
            type="checkbox"
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
          />
        </label>
        <label className="cd-settings2__toggle-row">
          <span>Reserva online</span>
          <input
            type="checkbox"
            checked={publicBooking}
            onChange={(event) => setPublicBooking(event.target.checked)}
          />
        </label>
      </div>


      </section>
      <details className="cd-services-v4__advanced">
        <summary>Disponibilidad y profesionales</summary>
        <div className="cd-services-v4__advanced-inner">
      <div className="cd-settings2__pending-body">
        <SalonIcon name="storefront" />
        <div>
          <strong>Locales</strong>
          {settings.locations.map((location) => (
            <label key={location.id} className="cd-settings2__toggle-row">
              <span>{location.name}</span>
              <input
                type="checkbox"
                checked={locationIds.includes(location.id)}
                onChange={(event) =>
                  setLocationIds(
                    toggleId(
                      locationIds,
                      location.id,
                      event.target.checked,
                    ),
                  )
                }
              />
            </label>
          ))}
        </div>
      </div>

      <div className="cd-settings2__pending-body">
        <SalonIcon name="groups" />
        <div>
          <strong>Recursos elegibles</strong>
          {settings.resources
            .filter((resource) => resource.active)
            .map((resource) => (
              <label key={resource.id} className="cd-settings2__toggle-row">
                <span>
                  {resource.name} · {RESOURCE_LABELS[resource.resourceType]}
                </span>
                <input
                  type="checkbox"
                  checked={eligibleResourceIds.includes(resource.id)}
                  onChange={(event) =>
                    setEligibleResourceIds(
                      toggleId(
                        eligibleResourceIds,
                        resource.id,
                        event.target.checked,
                      ),
                    )
                  }
                />
              </label>
            ))}
        </div>
      </div>


        </div>
      </details>
      <details className="cd-services-v4__advanced">
        <summary>Opciones avanzadas y recursos requeridos</summary>
        <div className="cd-services-v4__advanced-inner">
          <h3 className="cd-services-v4__subheading">Tiempos de preparación</h3>
          <div className="cd-settings2__preview-form cd-services-v4__buffers">
        <label className="cd-settings2__field">
          <span className="cd-settings2__field-label">Buffer antes</span>
          <input
            className="cd-settings2__input"
            type="number"
            min={0}
            value={bufferBefore}
            onChange={(event) => setBufferBefore(event.target.value)}
          />
        </label>
        <label className="cd-settings2__field">
          <span className="cd-settings2__field-label">Buffer después</span>
          <input
            className="cd-settings2__input"
            type="number"
            min={0}
            value={bufferAfter}
            onChange={(event) => setBufferAfter(event.target.value)}
          />
        </label>

          </div>
      <div className="cd-settings2__connection-list">
        <strong>Recursos requeridos</strong>
        {requirements.map((requirement, index) => (
          <article
            key={`${requirement.requirementKey}:${index}`}
            className="cd-settings2__connection-card"
          >
            <div className="cd-settings2__preview-form">
              <label className="cd-settings2__field">
                <span className="cd-settings2__field-label">Clave</span>
                <input
                  className="cd-settings2__input"
                  value={requirement.requirementKey}
                  onChange={(event) =>
                    updateRequirement(index, {
                      requirementKey: event.target.value,
                    })
                  }
                />
              </label>
              <label className="cd-settings2__field">
                <span className="cd-settings2__field-label">Tipo</span>
                <select
                  className="cd-settings2__input"
                  value={requirement.resourceType ?? ""}
                  onChange={(event) =>
                    updateRequirement(index, {
                      resourceType:
                        (event.target.value ||
                          null) as SalonSchedulingResourceType | null,
                    })
                  }
                >
                  <option value="">Sólo capability</option>
                  {Object.entries(RESOURCE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="cd-settings2__field">
                <span className="cd-settings2__field-label">Capability</span>
                <input
                  className="cd-settings2__input"
                  value={requirement.capability ?? ""}
                  onChange={(event) =>
                    updateRequirement(index, {
                      capability: event.target.value.trim() || null,
                    })
                  }
                />
              </label>
              <label className="cd-settings2__field">
                <span className="cd-settings2__field-label">Cantidad</span>
                <input
                  className="cd-settings2__input"
                  type="number"
                  min={1}
                  max={32}
                  value={requirement.quantity}
                  onChange={(event) =>
                    updateRequirement(index, {
                      quantity: Number(event.target.value),
                    })
                  }
                />
              </label>
              <label className="cd-settings2__field">
                <span className="cd-settings2__field-label">Selección</span>
                <select
                  className="cd-settings2__input"
                  value={requirement.selectionMode}
                  onChange={(event) =>
                    updateRequirement(index, {
                      selectionMode: event.target
                        .value as SalonServiceResourceRequirement["selectionMode"],
                    })
                  }
                >
                  <option value="automatic">Automática</option>
                  <option value="operator_selectable">Operador</option>
                  <option value="customer_selectable">Cliente</option>
                </select>
              </label>
              <label className="cd-settings2__toggle-row">
                <span>Obligatorio</span>
                <input
                  type="checkbox"
                  checked={requirement.required}
                  onChange={(event) =>
                    updateRequirement(index, {
                      required: event.target.checked,
                    })
                  }
                />
              </label>
              <button
                type="button"
                className="cd-settings2__module-cta"
                onClick={() =>
                  setRequirements(
                    requirements.filter((_, current) => current !== index),
                  )
                }
              >
                Quitar
              </button>
            </div>
          </article>
        ))}
        <button
          type="button"
          className="cd-settings2__module-cta"
          onClick={addRequirement}
        >
          + Recurso requerido
        </button>
      </div>


        </div>
      </details>
      <footer className="cd-settings2__preview-footer">
        <span className="cd-settings2__preview-status">
          Servicio · versión {service.version}
        </span>
        <button
          type="button"
          className="cd-settings2__preview-save"
          disabled={
            saving ||
            !name.trim() ||
            !Number.isSafeInteger(Number(duration)) ||
            Number(duration) < 5
          }
          onClick={() =>
            void onSave({
              name: name.trim(),
              description: description.trim() || null,
              durationMinutes: Number(duration),
              bufferBeforeMinutes: Number(bufferBefore),
              bufferAfterMinutes: Number(bufferAfter),
              priceMinor:
                price.trim() === ""
                  ? null
                  : Math.round(Number(price) * 100),
              currency: currency.trim().toUpperCase(),
              active,
              publicBookingEnabled: publicBooking,
              categoryId: categoryId || null,
              locationIds,
              eligibleResourceIds,
              resourceRequirements: requirements,
              expectedVersion: service.version,
            })
          }
        >
          {saving ? "Guardando…" : "Guardar servicio"}
        </button>
      </footer>
    </article>
  );
}

function TeamModule({
  settings,
  savingKey,
  mutate,
  api,
  organizationId,
  onBack,
}: {
  settings: SalonSchedulingSettings;
  savingKey: string | null;
  mutate: (key: string, operation: () => Promise<unknown>) => Promise<void>;
  api: PlatformApiClient;
  organizationId: string;
  onBack: () => void;
}) {
  const [newName, setNewName] = useState("");
  const [newType, setNewType] =
    useState<SalonSchedulingResourceType>("professional");
  const [newResourceOpen, setNewResourceOpen] = useState(false);
  function closeNewResource() {
    if (savingKey !== null) return;
    if ((newName.trim() || newType !== "professional") && !window.confirm("¿Descartar el recurso sin guardar?")) return;
    setNewName("");
    setNewType("professional");
    setNewResourceOpen(false);
  }
  return (
    <section className="cd-settings2 cd-settings2--module-state">
      <ModuleHeader
        title="Equipo y recursos"
        subtitle="Profesionales, cabinas, sillas y equipos que Scheduling puede reservar."
        onBack={onBack}
      />
      <div className="cd-services-v4__compact-grid">
        {settings.resources.map((resource) => (
          <ResourceEditor
            key={`${resource.id}:${resource.version}`}
            resource={resource}
            locations={settings.locations}
            saving={savingKey === `resource:${resource.id}`}
            onSave={(input) =>
              mutate(`resource:${resource.id}`, () =>
                api.updateSalonSchedulingResource(
                  organizationId,
                  resource.id,
                  input,
                ),
              )
            }
          />
        ))}
      </div>
      <button type="button" className="cd-btn-primary cd-settings2__new-resource-trigger"
        onClick={() => setNewResourceOpen(true)}>+ Agregar profesional o recurso</button>
      {newResourceOpen && <SalonsDrawer
        title="Nuevo profesional o recurso"
        eyebrow="Equipo y recursos"
        description="Agregá un profesional, cabina o equipo reservable."
        busy={savingKey === "resource:new"}
        onClose={closeNewResource}
      >
      <div className="cd-settings2__creation-body">
        <div className="cd-settings2__preview-form">
          <label className="cd-settings2__field">
            <span className="cd-settings2__field-label">Nombre</span>
            <input
              className="cd-settings2__input"
              value={newName}
              placeholder="Sofía"
              onChange={(event) => setNewName(event.target.value)}
            />
          </label>
          <label className="cd-settings2__field">
            <span className="cd-settings2__field-label">Tipo</span>
            <select
              className="cd-settings2__input"
              value={newType}
              onChange={(event) =>
                setNewType(event.target.value as SalonSchedulingResourceType)
              }
            >
              {Object.entries(RESOURCE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <footer className="cd-settings2__preview-footer">
          <span className="cd-settings2__preview-status">
            User y Professional siguen siendo entidades distintas.
          </span>
          <button
            type="button"
            className="cd-settings2__preview-save"
            disabled={savingKey !== null || !newName.trim()}
            onClick={() =>
              void mutate("resource:new", async () => {
                await api.createSalonSchedulingResource(organizationId, {
                  name: newName.trim(),
                  resourceType: newType,
                  locationIds: settings.locations
                    .filter((location) => location.active)
                    .map((location) => location.id),
                });
                setNewName("");
                setNewType("professional");
                setNewResourceOpen(false);
              })
            }
          >
            {savingKey === "resource:new" ? "Creando…" : "Crear recurso"}
          </button>
        </footer>
      </div>
      </SalonsDrawer>}
    </section>
  );
}

function ResourceEditor({
  resource,
  locations,
  saving,
  onSave,
}: {
  resource: SalonSchedulingResource;
  locations: readonly SalonSchedulingLocation[];
  saving: boolean;
  onSave: (
    input: Partial<Omit<SalonSchedulingResource, "id" | "version">> & {
      expectedVersion: number;
    },
  ) => Promise<void>;
}) {
  const [name, setName] = useState(resource.name);
  const [displayName, setDisplayName] = useState(resource.displayName ?? "");
  const [resourceType, setResourceType] =
    useState<SalonSchedulingResourceType>(resource.resourceType);
  const [active, setActive] = useState(resource.active);
  const [locationIds, setLocationIds] = useState<readonly string[]>(
    resource.locationIds,
  );
  const [capabilities, setCapabilities] = useState(
    resource.capabilities.join(", "),
  );

  return (
    <details className="cd-services-v4__compact-editor">
      <summary className="cd-services-v4__compact-summary">
        <span><strong>{resource.displayName || resource.name}</strong><small>{RESOURCE_LABELS[resource.resourceType]} · {resource.active ? "Activo" : "Inactivo"}</small></span>
        <span className="cd-services-v4__compact-edit-label">Editar</span>
      </summary><div className="cd-services-v4__compact-editor-body">
      <div className="cd-settings2__preview-form">
        <label className="cd-settings2__field">
          <span className="cd-settings2__field-label">Nombre interno</span>
          <input
            className="cd-settings2__input"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <label className="cd-settings2__field">
          <span className="cd-settings2__field-label">Nombre visible</span>
          <input
            className="cd-settings2__input"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </label>
        <label className="cd-settings2__field">
          <span className="cd-settings2__field-label">Tipo</span>
          <select
            className="cd-settings2__input"
            value={resourceType}
            onChange={(event) =>
              setResourceType(
                event.target.value as SalonSchedulingResourceType,
              )
            }
          >
            {Object.entries(RESOURCE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="cd-settings2__field">
          <span className="cd-settings2__field-label">Capabilities</span>
          <input
            className="cd-settings2__input"
            value={capabilities}
            placeholder="hair, color"
            onChange={(event) => setCapabilities(event.target.value)}
          />
        </label>
        <label className="cd-settings2__toggle-row">
          <span>Activo</span>
          <input
            type="checkbox"
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
          />
        </label>
      </div>
      <div className="cd-settings2__pending-body">
        <SalonIcon name="storefront" />
        <div>
          <strong>Locales</strong>
          {locations.map((location) => (
            <label key={location.id} className="cd-settings2__toggle-row">
              <span>{location.name}</span>
              <input
                type="checkbox"
                checked={locationIds.includes(location.id)}
                onChange={(event) =>
                  setLocationIds(
                    toggleId(
                      locationIds,
                      location.id,
                      event.target.checked,
                    ),
                  )
                }
              />
            </label>
          ))}
        </div>
      </div>
      <footer className="cd-settings2__preview-footer">
        <span className="cd-settings2__preview-status">
          Recurso · versión {resource.version}
        </span>
        <button
          type="button"
          className="cd-settings2__preview-save"
          disabled={saving || !name.trim()}
          onClick={() =>
            void onSave({
              name: name.trim(),
              displayName: displayName.trim() || null,
              resourceType,
              active,
              locationIds,
              capabilities: capabilities
                .split(",")
                .map((value) => value.trim())
                .filter(Boolean),
              expectedVersion: resource.version,
            })
          }
        >
          {saving ? "Guardando…" : "Guardar recurso"}
        </button>
      </footer>
      </div>
    </details>
  );
}

function HoursModule({
  settings,
  savingKey,
  mutate,
  api,
  organizationId,
  onBack,
}: {
  settings: SalonSchedulingSettings;
  savingKey: string | null;
  mutate: (key: string, operation: () => Promise<unknown>) => Promise<void>;
  api: PlatformApiClient;
  organizationId: string;
  onBack: () => void;
}) {
  const defaultLocation = settings.locations.find((location) => location.active);
  const [newRuleDay, setNewRuleDay] = useState("0");
  const [newRuleStart, setNewRuleStart] = useState("09:00");
  const [newRuleEnd, setNewRuleEnd] = useState("18:00");
  const [newRuleResource, setNewRuleResource] = useState("");
  const [newRuleOpen, setNewRuleOpen] = useState(false);
  const [newBlockOpen, setNewBlockOpen] = useState(false);
  const [newBlockResource, setNewBlockResource] = useState("");
  const [newBlockStart, setNewBlockStart] = useState("");
  const [newBlockEnd, setNewBlockEnd] = useState("");
  const [newBlockReason, setNewBlockReason] = useState("");
  function closeNewRule() {
    if (savingKey !== null) return;
    if ((newRuleDay !== "0" || newRuleStart !== "09:00" ||
      newRuleEnd !== "18:00" || newRuleResource) &&
      !window.confirm("¿Descartar el horario sin guardar?")) return;
    setNewRuleDay("0");
    setNewRuleStart("09:00");
    setNewRuleEnd("18:00");
    setNewRuleResource("");
    setNewRuleOpen(false);
  }
  function closeNewBlock() {
    if (savingKey !== null) return;
    if ((newBlockResource || newBlockStart || newBlockEnd || newBlockReason.trim()) &&
      !window.confirm("¿Descartar el bloqueo sin guardar?")) return;
    setNewBlockResource("");
    setNewBlockStart("");
    setNewBlockEnd("");
    setNewBlockReason("");
    setNewBlockOpen(false);
  }

  return (
    <section className="cd-settings2 cd-settings2--module-state">
      <ModuleHeader
        title="Agenda y horarios"
        subtitle="Horario semanal, disponibilidad profesional y excepciones consumidas por el motor canónico."
        onBack={onBack}
      />
      <div className="cd-services-v4__compact-grid">
        {settings.availabilityRules.map((rule) => (
          <AvailabilityRuleEditor
            key={`${rule.id}:${rule.version}`}
            rule={rule}
            settings={settings}
            saving={savingKey === `rule:${rule.id}`}
            onSave={(input) =>
              mutate(`rule:${rule.id}`, () =>
                api.updateSalonAvailabilityRule(
                  organizationId,
                  rule.id,
                  input,
                ),
              )
            }
          />
        ))}
      </div>

      <button type="button" className="cd-btn-primary cd-settings2__new-rule-trigger"
        onClick={() => setNewRuleOpen(true)}>+ Nuevo horario</button>
      {newRuleOpen && <SalonsDrawer
        title="Nuevo horario"
        eyebrow="Agenda y horarios"
        description="Sin recurso seleccionado configura el horario del local; con recurso restringe su disponibilidad."
        busy={savingKey === "rule:new"}
        onClose={closeNewRule}
      >
      <div className="cd-settings2__creation-body">
        <div className="cd-settings2__preview-form">
          <label className="cd-settings2__field">
            <span className="cd-settings2__field-label">Día</span>
            <select
              className="cd-settings2__input"
              value={newRuleDay}
              onChange={(event) => setNewRuleDay(event.target.value)}
            >
              {DAYS.map((day, index) => (
                <option key={day} value={index}>
                  {day}
                </option>
              ))}
            </select>
          </label>
          <label className="cd-settings2__field">
            <span className="cd-settings2__field-label">Desde</span>
            <input
              className="cd-settings2__input"
              type="time"
              value={newRuleStart}
              onChange={(event) => setNewRuleStart(event.target.value)}
            />
          </label>
          <label className="cd-settings2__field">
            <span className="cd-settings2__field-label">Hasta</span>
            <input
              className="cd-settings2__input"
              type="time"
              value={newRuleEnd}
              onChange={(event) => setNewRuleEnd(event.target.value)}
            />
          </label>
          <label className="cd-settings2__field">
            <span className="cd-settings2__field-label">
              Profesional / recurso
            </span>
            <select
              className="cd-settings2__input"
              value={newRuleResource}
              onChange={(event) => setNewRuleResource(event.target.value)}
            >
              <option value="">Horario del local</option>
              {settings.resources
                .filter((resource) => resource.active)
                .map((resource) => (
                  <option key={resource.id} value={resource.id}>
                    {resource.name}
                  </option>
                ))}
            </select>
          </label>
        </div>
        <footer className="cd-settings2__preview-footer">
          <span className="cd-settings2__preview-status">
            Zona horaria: {defaultLocation?.timezone ?? settings.business.timezone}
          </span>
          <button
            type="button"
            className="cd-settings2__preview-save"
            disabled={
              savingKey !== null ||
              !defaultLocation ||
              newRuleStart >= newRuleEnd
            }
            onClick={() =>
              defaultLocation &&
              void mutate("rule:new", async () => {
                await api.createSalonAvailabilityRule(organizationId, {
                  locationId: defaultLocation.id,
                  resourceId: newRuleResource || null,
                  dayOfWeek: Number(newRuleDay),
                  startTime: newRuleStart,
                  endTime: newRuleEnd,
                  slotIntervalMinutes: 15,
                  timezone: defaultLocation.timezone,
                  active: true,
                });
                setNewRuleDay("0");
                setNewRuleStart("09:00");
                setNewRuleEnd("18:00");
                setNewRuleResource("");
                setNewRuleOpen(false);
              })
            }
          >
            {savingKey === "rule:new" ? "Creando…" : "Agregar horario"}
          </button>
        </footer>
      </div></SalonsDrawer>}

      <div className="cd-settings2__group">
        <div className="cd-settings2__group-head">
          <h2 className="cd-settings2__group-title">Ausencias y bloqueos</h2>
          <p className="cd-settings2__group-hint">
            No modifican el horario recurrente.
          </p>
        </div>
        <div className="cd-settings2__connection-list">
          {settings.availabilityBlocks.map((block) => (
            <AvailabilityBlockEditor
              key={`${block.id}:${block.version}`}
              block={block}
              settings={settings}
              saving={savingKey === `block:${block.id}`}
              onSave={(input) =>
                mutate(`block:${block.id}`, () =>
                  api.updateSalonAvailabilityBlock(
                    organizationId,
                    block.id,
                    input,
                  ),
                )
              }
            />
          ))}
        </div>
        <button type="button" className="cd-btn-primary cd-settings2__new-block-trigger"
          onClick={() => setNewBlockOpen(true)}>+ Nuevo bloqueo</button>
        {newBlockOpen && <SalonsDrawer
          title="Nuevo bloqueo"
          eyebrow="Agenda y horarios"
          description="Indicá una ausencia sin cambiar el horario semanal."
          busy={savingKey === "block:new"}
          onClose={closeNewBlock}
        >
        <div className="cd-settings2__creation-body">
          <div className="cd-settings2__preview-form">
            <label className="cd-settings2__field">
              <span className="cd-settings2__field-label">Recurso</span>
              <select
                className="cd-settings2__input"
                value={newBlockResource}
                onChange={(event) => setNewBlockResource(event.target.value)}
              >
                <option value="">Todo el local</option>
                {settings.resources.map((resource) => (
                  <option key={resource.id} value={resource.id}>
                    {resource.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="cd-settings2__field">
              <span className="cd-settings2__field-label">Desde</span>
              <input
                className="cd-settings2__input"
                type="datetime-local"
                value={newBlockStart}
                onChange={(event) => setNewBlockStart(event.target.value)}
              />
            </label>
            <label className="cd-settings2__field">
              <span className="cd-settings2__field-label">Hasta</span>
              <input
                className="cd-settings2__input"
                type="datetime-local"
                value={newBlockEnd}
                onChange={(event) => setNewBlockEnd(event.target.value)}
              />
            </label>
            <label className="cd-settings2__field">
              <span className="cd-settings2__field-label">Motivo</span>
              <input
                className="cd-settings2__input"
                value={newBlockReason}
                onChange={(event) => setNewBlockReason(event.target.value)}
              />
            </label>
          </div>
          <footer className="cd-settings2__preview-footer">
            <span className="cd-settings2__preview-status">
              Se aplica a la misma disponibilidad usada por staff, web e IA.
            </span>
            <button
              type="button"
              className="cd-settings2__preview-save"
              disabled={
                savingKey !== null ||
                !newBlockStart ||
                !newBlockEnd ||
                newBlockStart >= newBlockEnd
              }
              onClick={() =>
                void mutate("block:new", async () => {
                  await api.createSalonAvailabilityBlock(organizationId, {
                    locationId: defaultLocation?.id ?? null,
                    resourceId: newBlockResource || null,
                    startsAt: dateTimeFromInput(newBlockStart),
                    endsAt: dateTimeFromInput(newBlockEnd),
                    reason: newBlockReason.trim() || null,
                    visibleLabel: newBlockReason.trim() || null,
                    blockType: "manual",
                    active: true,
                  });
                  setNewBlockStart("");
                  setNewBlockEnd("");
                  setNewBlockReason("");
                  setNewBlockResource("");
                  setNewBlockOpen(false);
                })
              }
            >
              {savingKey === "block:new" ? "Creando…" : "Crear bloqueo"}
            </button>
          </footer>
        </div></SalonsDrawer>}
      </div>
    </section>
  );
}

function AvailabilityRuleEditor({
  rule,
  settings,
  saving,
  onSave,
}: {
  rule: SalonAvailabilityRule;
  settings: SalonSchedulingSettings;
  saving: boolean;
  onSave: (
    input: Partial<Omit<SalonAvailabilityRule, "id" | "version">> & {
      expectedVersion: number;
    },
  ) => Promise<void>;
}) {
  const [day, setDay] = useState(String(rule.dayOfWeek));
  const [start, setStart] = useState(rule.startTime.slice(0, 5));
  const [end, setEnd] = useState(rule.endTime.slice(0, 5));
  const [resourceId, setResourceId] = useState(rule.resourceId ?? "");
  const [locationId, setLocationId] = useState(rule.locationId ?? "");
  const [active, setActive] = useState(rule.active);
  const resourceName =
    settings.resources.find((resource) => resource.id === rule.resourceId)
      ?.name ?? "Horario del local";

  return (
    <details className="cd-services-v4__compact-editor">
      <summary className="cd-services-v4__compact-summary">
        <span><strong>{DAYS[rule.dayOfWeek] ?? "Día"}</strong><small>{resourceName} · {rule.startTime.slice(0, 5)}–{rule.endTime.slice(0, 5)}</small></span>
        <span className="cd-services-v4__compact-edit-label">{active ? "Editar" : "Inactivo"}</span>
      </summary><div className="cd-services-v4__compact-editor-body">
      <div className="cd-settings2__preview-form">
        <select
          className="cd-settings2__input"
          value={day}
          onChange={(event) => setDay(event.target.value)}
          aria-label="Día"
        >
          {DAYS.map((label, index) => (
            <option key={label} value={index}>
              {label}
            </option>
          ))}
        </select>
        <input
          className="cd-settings2__input"
          type="time"
          value={start}
          onChange={(event) => setStart(event.target.value)}
          aria-label="Desde"
        />
        <input
          className="cd-settings2__input"
          type="time"
          value={end}
          onChange={(event) => setEnd(event.target.value)}
          aria-label="Hasta"
        />
        <select
          className="cd-settings2__input"
          value={locationId}
          onChange={(event) => setLocationId(event.target.value)}
          aria-label="Local"
        >
          <option value="">Sin local</option>
          {settings.locations.map((location) => (
            <option key={location.id} value={location.id}>
              {location.name}
            </option>
          ))}
        </select>
        <select
          className="cd-settings2__input"
          value={resourceId}
          onChange={(event) => setResourceId(event.target.value)}
          aria-label="Recurso"
        >
          <option value="">Horario del local</option>
          {settings.resources.map((resource) => (
            <option key={resource.id} value={resource.id}>
              {resource.name}
            </option>
          ))}
        </select>
        <label className="cd-settings2__toggle-row">
          <span>Activo</span>
          <input
            type="checkbox"
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
          />
        </label>
      </div>
      <footer className="cd-settings2__preview-footer">
        <span className="cd-settings2__preview-status">
          Regla · versión {rule.version}
        </span>
        <button
          type="button"
          className="cd-settings2__preview-save"
          disabled={saving || start >= end}
          onClick={() =>
            void onSave({
              dayOfWeek: Number(day),
              startTime: start,
              endTime: end,
              locationId: locationId || null,
              resourceId: resourceId || null,
              active,
              expectedVersion: rule.version,
            })
          }
        >
          {saving ? "Guardando…" : "Guardar horario"}
        </button>
      </footer>
      </div>
    </details>
  );
}

function AvailabilityBlockEditor({
  block,
  settings,
  saving,
  onSave,
}: {
  block: SalonAvailabilityBlock;
  settings: SalonSchedulingSettings;
  saving: boolean;
  onSave: (
    input: Partial<Omit<SalonAvailabilityBlock, "id" | "version">> & {
      expectedVersion: number;
    },
  ) => Promise<void>;
}) {
  const [startsAt, setStartsAt] = useState(inputDateTime(block.startsAt));
  const [endsAt, setEndsAt] = useState(inputDateTime(block.endsAt));
  const [reason, setReason] = useState(block.reason ?? "");
  const [resourceId, setResourceId] = useState(block.resourceId ?? "");
  const [active, setActive] = useState(block.active);

  return (
    <article className="cd-settings2__connection-card">
      <div>
        <SalonIcon name="block" />
        <div>
          <strong>{block.visibleLabel || block.reason || "Bloqueo"}</strong>
          <span>
            {formatDate(block.startsAt)} → {formatDate(block.endsAt)}
          </span>
        </div>
      </div>
      <div className="cd-settings2__preview-form">
        <select
          className="cd-settings2__input"
          value={resourceId}
          onChange={(event) => setResourceId(event.target.value)}
          aria-label="Recurso"
        >
          <option value="">Todo el local</option>
          {settings.resources.map((resource) => (
            <option key={resource.id} value={resource.id}>
              {resource.name}
            </option>
          ))}
        </select>
        <input
          className="cd-settings2__input"
          type="datetime-local"
          value={startsAt}
          onChange={(event) => setStartsAt(event.target.value)}
          aria-label="Desde"
        />
        <input
          className="cd-settings2__input"
          type="datetime-local"
          value={endsAt}
          onChange={(event) => setEndsAt(event.target.value)}
          aria-label="Hasta"
        />
        <input
          className="cd-settings2__input"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          aria-label="Motivo"
        />
        <label className="cd-settings2__toggle-row">
          <span>Activo</span>
          <input
            type="checkbox"
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
          />
        </label>
        <button
          type="button"
          className="cd-settings2__preview-save"
          disabled={saving || !startsAt || !endsAt || startsAt >= endsAt}
          onClick={() =>
            void onSave({
              resourceId: resourceId || null,
              startsAt: dateTimeFromInput(startsAt),
              endsAt: dateTimeFromInput(endsAt),
              reason: reason.trim() || null,
              visibleLabel: reason.trim() || null,
              active,
              expectedVersion: block.version,
            })
          }
        >
          {saving ? "Guardando…" : "Guardar"}
        </button>
      </div>
    </article>
  );
}

function BookingRulesModule({
  settings,
  savingKey,
  mutate,
  api,
  organizationId,
  publicBookingPath,
  onBack,
}: {
  settings: SalonSchedulingSettings;
  savingKey: string | null;
  mutate: (key: string, operation: () => Promise<unknown>) => Promise<void>;
  api: PlatformApiClient;
  organizationId: string;
  publicBookingPath: string;
  onBack: () => void;
}) {
  return (
    <section className="cd-settings2 cd-settings2--module-state">
      <ModuleHeader
        title="Reglas de reserva"
        subtitle="Una sola política para recepción, reserva online, IA y Voice."
        onBack={onBack}
      />
      <BookingRulesEditor
        rules={settings.bookingRules}
        saving={savingKey === "booking-rules"}
        onSave={(input) =>
          mutate("booking-rules", () =>
            api.updateSalonBookingRules(organizationId, input),
          )
        }
      />
      <PublicBookingEditor
        settings={settings.publicBooking}
        publicBookingPath={publicBookingPath}
        saving={savingKey === "public-booking"}
        onSave={(input) =>
          mutate("public-booking", () =>
            api.updateSalonPublicBooking(organizationId, input),
          )
        }
      />
    </section>
  );
}

function BookingRulesEditor({
  rules,
  saving,
  onSave,
}: {
  rules: SalonBookingRules;
  saving: boolean;
  onSave: (
    input: Partial<Omit<SalonBookingRules, "version">> & {
      expectedVersion: number;
    },
  ) => Promise<void>;
}) {
  const [minimumNotice, setMinimumNotice] = useState(
    String(rules.minimumNoticeMinutes),
  );
  const [horizon, setHorizon] = useState(String(rules.bookingHorizonDays));
  const [cancelCutoff, setCancelCutoff] = useState(
    String(rules.cancellationCutoffMinutes),
  );
  const [rescheduleCutoff, setRescheduleCutoff] = useState(
    String(rules.rescheduleCutoffMinutes),
  );
  const [allowProfessional, setAllowProfessional] = useState(
    rules.allowProfessionalSelection,
  );
  const [autoAssign, setAutoAssign] = useState(rules.autoAssign);
  const [confirmation, setConfirmation] =
    useState<SalonBookingRules["confirmationMode"]>(rules.confirmationMode);
  const [noAvailability, setNoAvailability] =
    useState<SalonBookingRules["noAvailabilityBehavior"]>(
      rules.noAvailabilityBehavior,
    );
  const [overbooking, setOverbooking] = useState(rules.overbookingAllowed);
  const [depositMode, setDepositMode] =
    useState<SalonBookingRules["depositMode"]>(rules.depositMode);
  const [depositValue, setDepositValue] = useState(String(rules.depositValue));

  return (
    <section className="cd-settings2__module-preview">
      <header className="cd-settings2__preview-head">
        <div>
          <h2 className="cd-settings2__preview-title">Política canónica</h2>
          <p className="cd-settings2__preview-sub">
            Scheduling debe hacer cumplir estas reglas; no son sólo texto de
            interfaz.
          </p>
        </div>
      </header>
      <div className="cd-settings2__preview-form">
        <label className="cd-settings2__field">
          <span className="cd-settings2__field-label">
            Anticipación mínima (min)
          </span>
          <input
            className="cd-settings2__input"
            type="number"
            min={0}
            value={minimumNotice}
            onChange={(event) => setMinimumNotice(event.target.value)}
          />
        </label>
        <label className="cd-settings2__field">
          <span className="cd-settings2__field-label">
            Horizonte (días)
          </span>
          <input
            className="cd-settings2__input"
            type="number"
            min={1}
            value={horizon}
            onChange={(event) => setHorizon(event.target.value)}
          />
        </label>
        <label className="cd-settings2__field">
          <span className="cd-settings2__field-label">
            Cancelación hasta (min antes)
          </span>
          <input
            className="cd-settings2__input"
            type="number"
            min={0}
            value={cancelCutoff}
            onChange={(event) => setCancelCutoff(event.target.value)}
          />
        </label>
        <label className="cd-settings2__field">
          <span className="cd-settings2__field-label">
            Reprogramación hasta (min antes)
          </span>
          <input
            className="cd-settings2__input"
            type="number"
            min={0}
            value={rescheduleCutoff}
            onChange={(event) => setRescheduleCutoff(event.target.value)}
          />
        </label>
        <label className="cd-settings2__field">
          <span className="cd-settings2__field-label">Confirmación</span>
          <select
            className="cd-settings2__input"
            value={confirmation}
            onChange={(event) =>
              setConfirmation(
                event.target.value as SalonBookingRules["confirmationMode"],
              )
            }
          >
            <option value="automatic">Automática</option>
            <option value="pending">Pendiente</option>
          </select>
        </label>
        <label className="cd-settings2__field">
          <span className="cd-settings2__field-label">
            Sin disponibilidad
          </span>
          <select
            className="cd-settings2__input"
            value={noAvailability}
            onChange={(event) =>
              setNoAvailability(
                event.target.value as SalonBookingRules["noAvailabilityBehavior"],
              )
            }
          >
            <option value="alternatives">Ofrecer alternativas</option>
            <option value="human_handoff">Derivar a humano</option>
            <option value="reject">Rechazar</option>
          </select>
        </label>
        <label className="cd-settings2__field">
          <span className="cd-settings2__field-label">Depósito</span>
          <select
            className="cd-settings2__input"
            value={depositMode}
            onChange={(event) =>
              setDepositMode(
                event.target.value as SalonBookingRules["depositMode"],
              )
            }
          >
            <option value="none">No requerido</option>
            <option value="percentage">Porcentaje</option>
            <option value="fixed">Monto fijo</option>
          </select>
        </label>
        <label className="cd-settings2__field">
          <span className="cd-settings2__field-label">
            Valor depósito
          </span>
          <input
            className="cd-settings2__input"
            type="number"
            min={0}
            value={depositValue}
            disabled={depositMode === "none"}
            onChange={(event) => setDepositValue(event.target.value)}
          />
        </label>
        <label className="cd-settings2__toggle-row">
          <span>Cliente elige profesional</span>
          <input
            type="checkbox"
            checked={allowProfessional}
            onChange={(event) => setAllowProfessional(event.target.checked)}
          />
        </label>
        <label className="cd-settings2__toggle-row">
          <span>Autoasignar “Cualquiera”</span>
          <input
            type="checkbox"
            checked={autoAssign}
            onChange={(event) => setAutoAssign(event.target.checked)}
          />
        </label>
        <label className="cd-settings2__toggle-row">
          <span>Permitir overbooking</span>
          <input
            type="checkbox"
            checked={overbooking}
            onChange={(event) => setOverbooking(event.target.checked)}
          />
        </label>
      </div>
      <footer className="cd-settings2__preview-footer">
        <span className="cd-settings2__preview-status">
          Booking Rules · versión {rules.version}
        </span>
        <button
          type="button"
          className="cd-settings2__preview-save"
          disabled={saving}
          onClick={() =>
            void onSave({
              minimumNoticeMinutes: Number(minimumNotice),
              bookingHorizonDays: Number(horizon),
              cancellationCutoffMinutes: Number(cancelCutoff),
              rescheduleCutoffMinutes: Number(rescheduleCutoff),
              allowProfessionalSelection: allowProfessional,
              autoAssign,
              confirmationMode: confirmation,
              noAvailabilityBehavior: noAvailability,
              overbookingAllowed: overbooking,
              depositMode,
              depositValue:
                depositMode === "none" ? 0 : Number(depositValue),
              expectedVersion: rules.version,
            })
          }
        >
          {saving ? "Guardando…" : "Guardar reglas"}
        </button>
      </footer>
    </section>
  );
}

function PublicBookingEditor({
  settings,
  publicBookingPath,
  saving,
  onSave,
}: {
  settings: SalonPublicBookingSettings;
  publicBookingPath: string;
  saving: boolean;
  onSave: (
    input: Partial<Omit<SalonPublicBookingSettings, "version">> & {
      expectedVersion: number;
    },
  ) => Promise<void>;
}) {
  const [enabled, setEnabled] = useState(settings.enabled);
  const [showPrices, setShowPrices] = useState(settings.showPrices);
  const [allowProfessional, setAllowProfessional] = useState(
    settings.allowProfessionalSelection,
  );
  const [requirePhone, setRequirePhone] = useState(
    settings.requireCustomerPhone,
  );

  return (
    <section className="cd-settings2__module-preview">
      <header className="cd-settings2__preview-head">
        <div>
          <h2 className="cd-settings2__preview-title">Reserva online</h2>
          <p className="cd-settings2__preview-sub">
            Usa el mismo Scheduling Core que recepción y la IA.
          </p>
        </div>
      </header>
      <div className="cd-settings2__status-card">
        <strong>Enlace público</strong>
        <p>{publicBookingPath}</p>
        <div className="cd-settings2__inline-actions">
          <a
            className="cd-settings2__module-cta"
            href={publicBookingPath}
            target="_blank"
            rel="noreferrer"
          >
            Abrir reserva pública
          </a>
          <button
            type="button"
            className="cd-settings2__module-cta"
            onClick={() => void navigator.clipboard?.writeText(window.location.origin + publicBookingPath)}
          >
            Copiar enlace
          </button>
        </div>
      </div>
      <div className="cd-settings2__preview-form">
        <label className="cd-settings2__toggle-row">
          <span>Activa</span>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
          />
        </label>
        <label className="cd-settings2__toggle-row">
          <span>Mostrar precios</span>
          <input
            type="checkbox"
            checked={showPrices}
            onChange={(event) => setShowPrices(event.target.checked)}
          />
        </label>
        <label className="cd-settings2__toggle-row">
          <span>Permitir elegir profesional</span>
          <input
            type="checkbox"
            checked={allowProfessional}
            onChange={(event) => setAllowProfessional(event.target.checked)}
          />
        </label>
        <label className="cd-settings2__toggle-row">
          <span>Teléfono obligatorio</span>
          <input
            type="checkbox"
            checked={requirePhone}
            onChange={(event) => setRequirePhone(event.target.checked)}
          />
        </label>
      </div>
      <footer className="cd-settings2__preview-footer">
        <span className="cd-settings2__preview-status">
          Public Booking · versión {settings.version}
        </span>
        <button
          type="button"
          className="cd-settings2__preview-save"
          disabled={saving}
          onClick={() =>
            void onSave({
              enabled,
              showPrices,
              allowProfessionalSelection: allowProfessional,
              requireCustomerPhone: requirePhone,
              expectedVersion: settings.version,
            })
          }
        >
          {saving ? "Guardando…" : "Guardar reserva online"}
        </button>
      </footer>
    </section>
  );
}

function WhatsAppModule({
  connections,
  unavailable,
  loading,
  error,
  onBack,
  onOpenPlatform,
}: {
  connections: SalonMetaWhatsAppConnections | null;
  unavailable: boolean;
  loading: boolean;
  error: string | null;
  onBack: () => void;
  onOpenPlatform: () => void;
}) {
  const active =
    connections?.connections.filter((item) => item.status === "active") ?? [];
  const revoked =
    connections?.connections.filter((item) => item.status === "revoked") ?? [];

  return (
    <section
      className="cd-settings2 cd-settings2--module-state"
      aria-label="WhatsApp e IA"
    >
      <ModuleHeader
        title="WhatsApp"
        subtitle="Estado real de la conexión Meta para este negocio."
        onBack={onBack}
      />
      <section className="cd-settings2__module-preview">
        <header className="cd-settings2__preview-head">
          <div>
            <h2 className="cd-settings2__preview-title">Meta WhatsApp</h2>
            <p className="cd-settings2__preview-sub">
              La conexión y las credenciales siguen protegidas por Platform.
            </p>
          </div>
          <span
            className={`cd-settings2__state cd-settings2__state--${active.length > 0 ? "complete" : unavailable ? "unavailable" : "attention"}`}
          >
            {loading
              ? "Leyendo…"
              : active.length > 0
                ? "Conectado"
                : unavailable
                  ? "No disponible"
                  : "Sin conexión activa"}
          </span>
        </header>

        {error && !unavailable && (
          <div className="inline-alert" role="alert">
            {error}
          </div>
        )}

        <div className="cd-settings2__connection-list">
          {loading && <p>Cargando conexiones…</p>}
          {!loading && unavailable && (
            <p>
              El storage protegido de conexiones Meta no está disponible en
              este entorno.
            </p>
          )}
          {!loading &&
            !unavailable &&
            active.map((item) => (
              <article
                key={item.id}
                className="cd-settings2__connection-card"
              >
                <div>
                  <SalonIcon name="chat" />
                  <div>
                    <strong>WhatsApp activo</strong>
                    <span>Conectado {formatDate(item.connectedAt)}</span>
                  </div>
                </div>
                <dl>
                  <div>
                    <dt>Proveedor</dt>
                    <dd>Meta WhatsApp</dd>
                  </div>
                  <div>
                    <dt>Estado</dt>
                    <dd>{item.status}</dd>
                  </div>
                  <div>
                    <dt>Revisión</dt>
                    <dd>{item.revision}</dd>
                  </div>
                  <div>
                    <dt>Actualizado</dt>
                    <dd>{formatDate(item.updatedAt)}</dd>
                  </div>
                </dl>
              </article>
            ))}
          {!loading && !unavailable && active.length === 0 && (
            <div className="cd-settings2__pending-body">
              <SalonIcon name="link_off" />
              <div>
                <strong>No hay conexión WhatsApp activa.</strong>
                <p>
                  La activación se gestiona en la superficie Platform
                  certificada.
                </p>
              </div>
            </div>
          )}
          {!loading && revoked.length > 0 && (
            <p className="cd-settings2__history-note">
              {revoked.length} conexión
              {revoked.length === 1 ? "" : "es"} revocada
              {revoked.length === 1 ? "" : "s"} en el historial.
            </p>
          )}
        </div>

        <footer className="cd-settings2__preview-footer">
          <span className="cd-settings2__preview-status">
            Sin tokens ni secretos expuestos en el browser.
          </span>
          <button
            type="button"
            className="cd-settings2__preview-save"
            onClick={onOpenPlatform}
          >
            Abrir configuración Platform
          </button>
        </footer>
      </section>
    </section>
  );
}

function CalComPanel({
  api,
  organizationId,
  returnPath,
  services,
}: {
  api: PlatformApiClient;
  organizationId: string;
  returnPath: string;
  services: readonly SalonSchedulingServiceConfig[];
}) {
  const [connection, setConnection] = useState<SalonCalComConnection | null>(null);
  const [eventTypes, setEventTypes] = useState<readonly SalonCalComEventType[]>([]);
  const [bindings, setBindings] = useState<readonly SalonCalComServiceBinding[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const nextConnection = await api.salonCalComConnection(organizationId);
      setConnection(nextConnection);
      if (nextConnection?.status === "valid") {
        const [nextTypes, nextBindings] = await Promise.all([
          api.salonCalComEventTypes(organizationId),
          api.salonCalComServiceBindings(organizationId),
        ]);
        setEventTypes(nextTypes);
        setBindings(nextBindings);
      } else {
        setEventTypes([]);
        setBindings([]);
      }
    } catch (cause) {
      if (
        cause instanceof PlatformApiError &&
        (cause.code === "calcom_onboarding_unavailable" ||
          cause.code === "calcom_connection_missing")
      ) {
        setConnection(null);
        setEventTypes([]);
        setBindings([]);
      } else {
        setError(errorText(cause));
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, [api, organizationId]);

  const bindingFor = (serviceId: string) =>
    bindings.find(
      (binding) =>
        binding.clickdialogServiceId === serviceId && binding.active,
    ) ?? null;

  async function act(key: string, operation: () => Promise<unknown>) {
    setBusy(key);
    setError(null);
    try {
      await operation();
      await refresh();
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="cd-settings2__module-preview">
      <header className="cd-settings2__preview-head">
        <div>
          <h2 className="cd-settings2__preview-title">Cal.com</h2>
          <p className="cd-settings2__preview-sub">
            Adaptador opcional. ClickDialog Scheduling conserva turnos,
            recursos y disponibilidad como verdad canónica.
          </p>
        </div>
        <span
          className={`cd-settings2__state cd-settings2__state--${
            connection?.status === "valid"
              ? "complete"
              : connection?.status === "error"
                ? "attention"
                : "support"
          }`}
        >
          {loading
            ? "Leyendo…"
            : connection?.status === "valid"
              ? "Conectado"
              : connection?.status === "error"
                ? "Requiere atención"
                : "Opcional"}
        </span>
      </header>

      {error && (
        <div className="inline-alert" role="alert">
          {error}
        </div>
      )}

      {!loading && !connection && (
        <div className="cd-settings2__pending-body">
          <SalonIcon name="calendar_month" />
          <div>
            <strong>Sin calendario externo.</strong>
            <p>
              El salón puede operar completamente con Scheduling sin Cal.com.
            </p>
            <button
              type="button"
              className="cd-settings2__preview-save"
              disabled={busy !== null}
              onClick={() =>
                void act("calcom:oauth", async () => {
                  const result = await api.startSalonCalComOAuth(
                    organizationId,
                    returnPath,
                  );
                  window.location.assign(result.authorizationUrl);
                })
              }
            >
              {busy === "calcom:oauth" ? "Conectando…" : "Conectar Cal.com"}
            </button>
          </div>
        </div>
      )}

      {connection && (
        <>
          <article className="cd-settings2__connection-card">
            <div>
              <SalonIcon name="calendar_month" />
              <div>
                <strong>
                  {connection.externalAccountName ??
                    connection.externalAccountEmail ??
                    connection.externalAccountUsername ??
                    "Cuenta Cal.com"}
                </strong>
                <span>
                  {connection.authType === "oauth" ? "OAuth" : "API key protegida"} ·{" "}
                  {connection.status}
                </span>
              </div>
            </div>
            <dl>
              <div>
                <dt>Última validación</dt>
                <dd>{formatDate(connection.lastValidatedAt)}</dd>
              </div>
              <div>
                <dt>Zona horaria</dt>
                <dd>{connection.externalAccountTimeZone ?? "—"}</dd>
              </div>
              {connection.lastErrorMessage && (
                <div>
                  <dt>Error</dt>
                  <dd>{connection.lastErrorMessage}</dd>
                </div>
              )}
            </dl>
            <div className="cd-settings2__preview-form">
              {connection.status !== "revoked" && (
                <button
                  type="button"
                  className="cd-settings2__module-cta"
                  disabled={busy !== null}
                  onClick={() =>
                    void act("calcom:validate", () =>
                      api.validateSalonCalComConnection(organizationId),
                    )
                  }
                >
                  Validar conexión
                </button>
              )}
              {connection.status !== "valid" && (
                <button
                  type="button"
                  className="cd-settings2__module-cta"
                  disabled={busy !== null}
                  onClick={() =>
                    void act("calcom:oauth", async () => {
                      const result = await api.startSalonCalComOAuth(
                        organizationId,
                        returnPath,
                      );
                      window.location.assign(result.authorizationUrl);
                    })
                  }
                >
                  Reconectar Cal.com
                </button>
              )}
              {connection.status !== "revoked" && (
                <button
                  type="button"
                  className="cd-settings2__module-cta"
                  disabled={busy !== null}
                  onClick={() =>
                    void act("calcom:disconnect", () =>
                      api.disconnectSalonCalCom(organizationId),
                    )
                  }
                >
                  Desconectar
                </button>
              )}
            </div>
          </article>

          {connection.status === "valid" && (
            <div className="cd-settings2__connection-list">
              <strong>Bindings de servicios</strong>
              {services.map((service) => {
                const binding = bindingFor(service.id);
                return (
                  <article
                    key={service.id}
                    className="cd-settings2__connection-card"
                  >
                    <div>
                      <SalonIcon name="content_cut" />
                      <div>
                        <strong>{service.name}</strong>
                        <span>
                          {binding
                            ? `Event type ${binding.externalEventTypeId}`
                            : "Sin binding externo"}
                        </span>
                      </div>
                    </div>
                    <select
                      className="cd-settings2__input"
                      value={binding?.externalEventTypeId ?? ""}
                      disabled={busy !== null}
                      aria-label={`Cal.com para ${service.name}`}
                      onChange={(event) => {
                        const value = event.target.value;
                        void act(`calcom:binding:${service.id}`, () =>
                          value
                            ? api.bindSalonServiceToCalCom(
                                organizationId,
                                service.id,
                                value,
                              )
                            : api.unbindSalonServiceFromCalCom(
                                organizationId,
                                service.id,
                              ),
                        );
                      }}
                    >
                      <option value="">Sin binding</option>
                      {eventTypes.map((eventType) => (
                        <option
                          key={eventType.externalEventTypeId}
                          value={eventType.externalEventTypeId}
                        >
                          {eventType.title}
                          {eventType.lengthInMinutes
                            ? ` · ${eventType.lengthInMinutes} min`
                            : ""}
                        </option>
                      ))}
                    </select>
                  </article>
                );
              })}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function IntegrationsModule({
  settings,
  overview,
  whatsapp,
  connections,
  connectionsLoading,
  api,
  organizationId,
  returnPath,
  onBack,
  onWhatsApp,
}: {
  settings: SalonSchedulingSettings;
  overview: SalonOverview;
  whatsapp: ReturnType<typeof connectionState>;
  connections: SalonMetaWhatsAppConnections | null;
  connectionsLoading: boolean;
  api: PlatformApiClient;
  organizationId: string;
  returnPath: string;
  onBack: () => void;
  onWhatsApp: () => void;
}) {
  return (
    <section className="cd-settings2 cd-settings2--module-state">
      <ModuleHeader
        title="Conexiones"
        subtitle="Estado operacional de calendarios y canales; ningún proveedor se vuelve dueño del dominio."
        onBack={onBack}
      />
      <CalComPanel
        api={api}
        organizationId={organizationId}
        returnPath={returnPath}
        services={settings.services}
      />
      <section className="cd-settings2__module-preview">
        <div className="cd-settings2__connection-list">
          {settings.calendarConnections.length === 0 && (
            <article className="cd-settings2__connection-card">
              <div>
                <SalonIcon name="calendar_month" />
                <div>
                  <strong>ClickDialog Scheduling</strong>
                  <span>Canónico · sin calendario externo conectado</span>
                </div>
              </div>
            </article>
          )}
          {settings.calendarConnections.map((connection) => (
            <article
              key={`${connection.provider}:${connection.updatedAt}`}
              className="cd-settings2__connection-card"
            >
              <div>
                <SalonIcon name="calendar_month" />
                <div>
                  <strong>{connection.provider}</strong>
                  <span>{connection.status}</span>
                </div>
              </div>
              <dl>
                <div>
                  <dt>Cuenta</dt>
                  <dd>
                    {connection.accountEmail ??
                      connection.accountUsername ??
                      "—"}
                  </dd>
                </div>
                <div>
                  <dt>Zona horaria</dt>
                  <dd>{connection.timezone ?? overview.timeZone}</dd>
                </div>
                <div>
                  <dt>Actualizado</dt>
                  <dd>{formatDate(connection.updatedAt)}</dd>
                </div>
              </dl>
            </article>
          ))}
          <article className="cd-settings2__connection-card">
            <div>
              <SalonIcon name="chat" />
              <div>
                <strong>Meta WhatsApp</strong>
                <span>{connectionsLoading ? "Leyendo…" : whatsapp.label}</span>
              </div>
            </div>
            <dl>
              <div>
                <dt>Conexiones activas</dt>
                <dd>
                  {connections?.connections.filter(
                    (item) => item.status === "active",
                  ).length ?? 0}
                </dd>
              </div>
            </dl>
            <button
              type="button"
              className="cd-settings2__module-cta"
              onClick={onWhatsApp}
            >
              Ver WhatsApp
            </button>
          </article>
        </div>
      </section>
    </section>
  );
}

export function SalonsSettings({
  organization,
  nestedPath,
  overview,
  api,
  navigate,
  onExpired,
  basePath,
}: {
  organization: OrganizationContext;
  nestedPath: string;
  overview: SalonOverview;
  api: PlatformApiClient;
  navigate: (path: string) => void;
  onExpired: () => void;
  basePath?: string;
}) {
  const orgId = organization.organization.id;
  const slug = organization.organization.slug;
  const base = basePath ?? `/o/${slug}/salons`;
  const platformHomePath = `/o/${slug}/home`;
  const platformSettingsPath = `/o/${slug}/settings`;
  const parts = nestedPath.replace(/^\/+/, "").split("/").filter(Boolean);
  const legacyServices = parts[0] === "services";
  const moduleKey = legacyServices
    ? "services"
    : parts[0] === "settings"
      ? parts[1] ?? "hub"
      : "hub";

  const [settings, setSettings] =
    useState<SalonSchedulingSettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const [connections, setConnections] =
    useState<SalonMetaWhatsAppConnections | null>(null);
  const [connectionsLoading, setConnectionsLoading] = useState(false);
  const [connectionsUnavailable, setConnectionsUnavailable] = useState(false);
  const [connectionsError, setConnectionsError] = useState<string | null>(null);
  const needsWhatsAppConnections =
    moduleKey === "hub" ||
    moduleKey === "whatsapp" ||
    moduleKey === "integrations";

  async function reloadSettings(): Promise<SalonSchedulingSettings> {
    const next = await api.salonSchedulingSettings(orgId);
    setSettings(next);
    return next;
  }

  useEffect(() => {
    let current = true;
    setSettingsLoading(true);
    setSettingsError(null);
    api
      .salonSchedulingSettings(orgId)
      .then((next) => {
        if (current) setSettings(next);
      })
      .catch((cause) => {
        if (!current) return;
        if (cause instanceof PlatformApiError && cause.status === 401) {
          onExpired();
          return;
        }
        setSettingsError(errorText(cause));
      })
      .finally(() => {
        if (current) setSettingsLoading(false);
      });
    return () => {
      current = false;
    };
  }, [api, orgId, onExpired]);

  useEffect(() => {
    let current = true;

    if (!needsWhatsAppConnections) {
      setConnectionsLoading(false);
      setConnectionsUnavailable(false);
      setConnectionsError(null);
      setConnections(null);
      return () => {
        current = false;
      };
    }

    setConnectionsLoading(true);
    setConnectionsUnavailable(false);
    setConnectionsError(null);
    api
      .salonMetaWhatsAppConnections(orgId)
      .then((next) => {
        if (current) setConnections(next);
      })
      .catch((cause) => {
        if (!current) return;
        if (cause instanceof PlatformApiError && cause.status === 401) {
          onExpired();
          return;
        }
        if (
          cause instanceof PlatformApiError &&
          cause.status === 503 &&
          cause.code === "integration_disabled"
        ) {
          setConnectionsUnavailable(true);
          setConnections({ connections: [] });
          return;
        }
        setConnectionsError(errorText(cause));
        setConnections({ connections: [] });
      })
      .finally(() => {
        if (current) setConnectionsLoading(false);
      });
    return () => {
      current = false;
    };
  }, [api, needsWhatsAppConnections, orgId, onExpired]);

  async function mutate(
    key: string,
    operation: () => Promise<unknown>,
  ): Promise<void> {
    setSavingKey(key);
    setSettingsError(null);
    try {
      await operation();
      await reloadSettings();
    } catch (cause) {
      if (cause instanceof PlatformApiError && cause.status === 401) {
        onExpired();
        return;
      }
      setSettingsError(errorText(cause));
    } finally {
      setSavingKey(null);
    }
  }

  const whatsapp = connectionState(connections, connectionsUnavailable);
  const scheduling = overview.integrations.scheduling;
  const salonProduct = organization.products.find(
    ({ manifest }) => manifest.routeBase === "salons",
  );
  const planKey = salonProduct?.access.organizationProduct?.planKey ?? null;
  const productAccess = salonProduct?.access.allowed === true;

  const readiness = useMemo(() => {
    if (!settings) {
      return {
        ready: 0,
        total: 5,
        business: false,
        locations: false,
        services: false,
        team: false,
        hours: false,
      };
    }
    const business = Boolean(
      settings.business.publicName && settings.business.timezone,
    );
    const locations = settings.locations.some((location) => location.active);
    const services = settings.services.some((service) => service.active);
    const team = settings.resources.some(
      (resource) =>
        resource.active && resource.resourceType === "professional",
    );
    const hours = settings.availabilityRules.some((rule) => rule.active);
    const flags = [business, locations, services, team, hours];
    return {
      ready: flags.filter(Boolean).length,
      total: flags.length,
      business,
      locations,
      services,
      team,
      hours,
    };
  }, [settings]);

  if (moduleKey === "whatsapp") {
    return (
      <WhatsAppModule
        connections={connections}
        unavailable={connectionsUnavailable}
        loading={connectionsLoading}
        error={connectionsError}
        onBack={() => navigate(`${base}/settings`)}
        onOpenPlatform={() => navigate(platformSettingsPath)}
      />
    );
  }

  if (!settings) {
    return (
      <section className="cd-settings2">
        <ModuleHeader
          title="Administración"
          subtitle="Configuración operativa de ClickDialog for Salons."
          onBack={() => navigate(base)}
        />
        <BusyError error={settingsError} loading={settingsLoading} />
        <button
          type="button"
          className="cd-settings2__preview-save"
          onClick={() => void reloadSettings()}
        >
          Reintentar
        </button>
      </section>
    );
  }

  if (moduleKey === "payments") {
    return (
      <PaymentsModule
        api={api}
        organizationId={orgId}
        settings={settings}
        savingKey={savingKey}
        mutate={mutate}
        onExpired={onExpired}
        onBack={() => navigate(`${base}/settings`)}
      />
    );
  }

  if (moduleKey === "automations") {
    return (
      <AutomationsModule
        api={api}
        organizationId={orgId}
        settings={settings}
        onExpired={onExpired}
        onBack={() => navigate(`${base}/settings`)}
      />
    );
  }

  if (moduleKey === "knowledge") {
    return (
      <KnowledgeModule
        api={api}
        organizationId={orgId}
        onExpired={onExpired}
        onBack={() => navigate(`${base}/settings`)}
      />
    );
  }

  if (moduleKey === "assistant") {
    return (
      <AssistantReadinessModule
        api={api}
        organizationId={orgId}
        onExpired={onExpired}
        onBack={() => navigate(`${base}/settings`)}
      />
    );
  }

  if (moduleKey === "business" || moduleKey === "locations") {
    return (
      <>
        <BusyError error={settingsError} />
        <BusinessModule
          key={`business:${settings.business.version}:${settings.locations.map((item) => item.version).join(".")}`}
          settings={settings}
          savingKey={savingKey}
          mutate={mutate}
          api={api}
          organizationId={orgId}
          onBack={() => navigate(`${base}/settings`)}
        />
      </>
    );
  }

  if (moduleKey === "services") {
    return (
      <>
        <BusyError error={settingsError} />
        <ServicesModule
          key={`services:${settings.services.map((item) => item.version).join(".")}:${settings.categories.map((item) => item.version).join(".")}`}
          settings={settings}
          savingKey={savingKey}
          mutate={mutate}
          api={api}
          organizationId={orgId}
          onBack={() => navigate(`${base}/settings`)}
        />
      </>
    );
  }

  if (moduleKey === "team" || moduleKey === "resources") {
    return (
      <>
        <BusyError error={settingsError} />
        <TeamModule
          key={`team:${settings.resources.map((item) => item.version).join(".")}`}
          settings={settings}
          savingKey={savingKey}
          mutate={mutate}
          api={api}
          organizationId={orgId}
          onBack={() => navigate(`${base}/settings`)}
        />
      </>
    );
  }

  if (moduleKey === "hours") {
    return (
      <>
        <BusyError error={settingsError} />
        <HoursModule
          key={`hours:${settings.availabilityRules.map((item) => item.version).join(".")}:${settings.availabilityBlocks.map((item) => item.version).join(".")}`}
          settings={settings}
          savingKey={savingKey}
          mutate={mutate}
          api={api}
          organizationId={orgId}
          onBack={() => navigate(`${base}/settings`)}
        />
      </>
    );
  }

  if (moduleKey === "booking-rules" || moduleKey === "public-booking") {
    return (
      <>
        <BusyError error={settingsError} />
        <BookingRulesModule
          key={`booking:${settings.bookingRules.version}:${settings.publicBooking.version}`}
          settings={settings}
          savingKey={savingKey}
          mutate={mutate}
          api={api}
          organizationId={orgId}
          publicBookingPath={`/salons/${slug}/book`}
          onBack={() => navigate(`${base}/settings`)}
        />
      </>
    );
  }

  if (moduleKey === "integrations") {
    return (
      <IntegrationsModule
        settings={settings}
        overview={overview}
        whatsapp={whatsapp}
        connections={connections}
        connectionsLoading={connectionsLoading}
        api={api}
        organizationId={orgId}
        returnPath={`${base}/settings/integrations`}
        onBack={() => navigate(`${base}/settings`)}
        onWhatsApp={() => navigate(`${base}/settings/whatsapp`)}
      />
    );
  }

  const activeServices = settings.services.filter((service) => service.active);
  const professionals = settings.resources.filter(
    (resource) => resource.active && resource.resourceType === "professional",
  );
  const activeLocations = settings.locations.filter(
    (location) => location.active,
  );
  const activeRules = settings.availabilityRules.filter((rule) => rule.active);
  const progress = Math.round((readiness.ready / readiness.total) * 100);

  const essentialModules: SettingsModule[] = [
    {
      key: "business",
      title: "Negocio y locales",
      description: `${settings.business.publicName} · ${activeLocations.length} local${activeLocations.length === 1 ? "" : "es"} activo${activeLocations.length === 1 ? "" : "s"}.`,
      impact: "Identidad operativa",
      state: readiness.business && readiness.locations ? "complete" : "attention",
      stateLabel:
        readiness.business && readiness.locations
          ? "Configurado"
          : "Requiere atención",
      cta: "Configurar",
      icon: "store",
      route: `${base}/settings/business`,
    },
    {
      key: "services",
      title: "Servicios",
      description: `${settings.services.length} servicio${settings.services.length === 1 ? "" : "s"} · ${activeServices.length} activo${activeServices.length === 1 ? "" : "s"}.`,
      impact: "Afecta agenda y reservas",
      state: readiness.services ? "complete" : "attention",
      stateLabel: readiness.services ? "Configurado" : "Requiere atención",
      cta: "Configurar",
      icon: "content_cut",
      route: `${base}/settings/services`,
    },
    {
      key: "team",
      title: "Equipo y recursos",
      description: `${professionals.length} profesional${professionals.length === 1 ? "" : "es"} · ${settings.resources.length} recurso${settings.resources.length === 1 ? "" : "s"} total.`,
      impact: "Impacta disponibilidad",
      state: readiness.team ? "complete" : "attention",
      stateLabel: readiness.team ? "Configurado" : "Requiere atención",
      cta: "Configurar",
      icon: "groups",
      route: `${base}/settings/team`,
    },
    {
      key: "hours",
      title: "Agenda y horarios",
      description: `${activeRules.length} regla${activeRules.length === 1 ? "" : "s"} de disponibilidad activa${activeRules.length === 1 ? "" : "s"}.`,
      impact: "Impacta todos los canales de reserva",
      state: readiness.hours ? "complete" : "attention",
      stateLabel: readiness.hours ? "Configurado" : "Requiere atención",
      cta: "Configurar",
      icon: "schedule",
      route: `${base}/settings/hours`,
    },
    {
      key: "booking-rules",
      title: "Reglas y reserva online",
      description: `Horizonte ${settings.bookingRules.bookingHorizonDays} días · reserva online ${settings.publicBooking.enabled ? "activa" : "inactiva"}.`,
      impact: "Impacta recepción, web, IA y Voice",
      state: "complete",
      stateLabel: "Editable",
      cta: "Configurar",
      icon: "rule",
      route: `${base}/settings/booking-rules`,
    },
    {
      key: "payments",
      title: "Pagos",
      description: `Depósito ${settings.bookingRules.depositMode === "none" ? "desactivado" : settings.bookingRules.depositMode} · Mercado Pago y métodos manuales.`,
      impact: "Ventas, depósitos y reembolsos",
      state: "complete",
      stateLabel: "Editable",
      cta: "Configurar",
      icon: "payments",
      route: `${base}/settings/payments`,
    },
    {
      key: "whatsapp",
      title: "WhatsApp",
      description: connectionsLoading ? "Leyendo estado Meta…" : whatsapp.description,
      impact: "Add-on de Conversaciones",
      state: connectionsLoading ? "support" : whatsapp.state,
      stateLabel: connectionsLoading ? "Leyendo…" : whatsapp.label,
      cta: "Ver estado",
      icon: "chat",
      route: `${base}/settings/whatsapp`,
    },
    {
      key: "integrations",
      title: "Conexiones",
      description: `${settings.calendarConnections.length} calendario${settings.calendarConnections.length === 1 ? "" : "s"} externo${settings.calendarConnections.length === 1 ? "" : "s"} · La agenda sigue sincronizada.`,
      impact: "Calendarios y proveedores",
      state: scheduling || whatsapp.active ? "support" : "complete",
      stateLabel: scheduling || whatsapp.active ? "Conectado" : "Sin externos",
      cta: "Ver conexiones",
      icon: "hub",
      route: `${base}/settings/integrations`,
    },
  ];

  const advancedModules: SettingsModule[] = [
    {
      key: "automations",
      title: "Automatizaciones",
      description: "Recordatorios, encuesta, reseña y recuperación no-show.",
      impact: "Automation Core canónico",
      state: "complete",
      stateLabel: "Editable",
      cta: "Configurar",
      icon: "notifications_active",
      route: `${base}/settings/automations`,
    },
    {
      key: "assistant",
      title: "Asistente IA",
      description: "Estado operativo del Agent Runtime sin exponer parámetros técnicos.",
      impact: "Agent Runtime Authority",
      state: "support",
      stateLabel: "Estado real",
      cta: "Ver estado",
      icon: "smart_toy",
      route: `${base}/settings/assistant`,
    },
    {
      key: "knowledge",
      title: "Conocimiento de la IA",
      description: "Fuentes, publicación, archivo y Knowledge Tester.",
      impact: "Platform Knowledge",
      state: "complete",
      stateLabel: "Editable",
      cta: "Configurar",
      icon: "menu_book",
      route: `${base}/settings/knowledge`,
    },
    {
      key: "account",
      title: "Usuarios y seguridad",
      description: "Identidad, membresías y permisos siguen siendo Platform.",
      impact: "Seguridad de acceso",
      state: "advanced",
      stateLabel: "Platform",
      cta: "Abrir mi cuenta",
      icon: "manage_accounts",
      externalRoute: "/account",
    },
    {
      key: "billing",
      title: "Plan y acceso",
      description: planKey
        ? `Plan de Peluquerías: ${planKey} · acceso ${productAccess ? "habilitado" : "restringido"}.`
        : `Acceso Peluquerías ${productAccess ? "habilitado" : "restringido"}.`,
      impact: "Producto y entitlements",
      state: productAccess ? "advanced" : "attention",
      stateLabel: productAccess ? "Platform" : "Revisar acceso",
      cta: "Volver a Plataforma",
      icon: "workspace_premium",
      externalRoute: platformHomePath,
    },
  ];

  return (
    <section className="cd-settings2" aria-labelledby="salons-settings-title">
      <header className="cd-settings2__hero">
        <div className="cd-settings2__hero-main">
          <div className="cd-settings2__hero-badges">
            <span className="cd-settings2__badge cd-settings2__badge--mvp">
              Configuración real
            </span>
            <span className="cd-settings2__badge cd-settings2__badge--lab">
              Scheduling V2
            </span>
          </div>
          <h1
            id="salons-settings-title"
            className="cd-settings2__hero-title"
          >
            Administración
          </h1>
          <p className="cd-settings2__hero-subtitle">
            Gestioná los servicios, horarios, el equipo y las conexiones de tu negocio.
          </p>
        </div>
      </header>

      <BusyError error={settingsError} loading={settingsLoading} />


      <div className="cd-settings2__group">
        <div className="cd-settings2__group-head">
          <h2 className="cd-settings2__group-title">Operación</h2>
          <p className="cd-settings2__group-hint">
            Opciones principales para gestionar el salón.
          </p>
        </div>
        <div className="cd-settings2__module-grid">
          {essentialModules.map((module) => (
            <ModuleCard
              key={module.key}
              module={module}
              navigate={navigate}
            />
          ))}
        </div>
      </div>

      <section
        className="cd-settings2__status-card"
        aria-labelledby="salons-settings-status-title"
      >
        <div className="cd-settings2__status-head">
          <div>
            <SalonIcon name="verified" className="cd-settings2__status-icon" />
            <h2
              id="salons-settings-status-title"
              className="cd-settings2__status-title"
            >
              Estado de configuración
            </h2>
            <p className="cd-settings2__status-progress">
              {readiness.ready} de {readiness.total} aspectos principales
              configurados
            </p>
          </div>
          <button
            type="button"
            className="cd-settings2__status-cta"
            onClick={() => navigate(`${base}/settings/hours`)}
          >
            Revisar horarios
          </button>
        </div>
        <div className="cd-settings2__status-bar" aria-hidden="true">
          <span
            className="cd-settings2__status-bar-fill"
            style={{ width: `${progress}%` }}
          />
        </div>
        <ul
          className="cd-settings2__checklist"
          aria-label="Readiness Scheduling"
        >
          {[
            ["Negocio", readiness.business],
            ["Local", readiness.locations],
            ["Servicios", readiness.services],
            ["Profesional", readiness.team],
            ["Horarios", readiness.hours],
          ].map(([label, done]) => (
            <li
              key={String(label)}
              className={
                done
                  ? "cd-settings2__check is-done"
                  : "cd-settings2__check is-pending"
              }
            >
              <SalonIcon
                name={done ? "check_circle" : "radio_button_unchecked"}
              />
              <span>{label}</span>
            </li>
          ))}
        </ul>
      </section>

      <details className="cd-salons-more-settings">
        <summary>Más configuraciones</summary>
        <div className="cd-settings2__group">
          <div className="cd-settings2__group-head">
            <h2 className="cd-settings2__group-title">Plataforma y capacidades avanzadas</h2>
            <p className="cd-settings2__group-hint">
              Salons compone estos dominios sin duplicar sus owners canónicos.
            </p>
          </div>
          <div className="cd-settings2__module-grid">
            {advancedModules.map((module) => (
              <ModuleCard
                key={module.key}
                module={module}
                navigate={navigate}
              />
            ))}
          </div>
        </div>
      </details>
    </section>
  );
}
